// Lightweight i18n dictionary for FR / EN / ES / IT / PT / DE
export const LANGUAGES = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "de", label: "Deutsch" },
];

const KEYS = {
  en: {
    app_name: "MerchantAudit",
    tagline: "Google Merchant Center compliance auditor",
    history: "Audit history",
    no_history: "No audits yet. Run your first scan.",
    input_mode_url: "URL",
    input_mode_text: "Raw text",
    input_mode_batch: "Batch",
    batch_mode_sitemap: "Sitemap",
    batch_mode_urls: "URL list",
    sitemap_url_placeholder: "https://yourstore.com/sitemap.xml",
    urls_placeholder: "One URL per line (max 50)...",
    max_urls: "Max URLs",
    url_placeholder: "https://yourstore.com/product/123",
    text_placeholder: "Paste page copy here...",
    page_type: "Page type",
    page_type_product: "Product",
    page_type_category: "Category",
    page_type_homepage: "Homepage",
    page_type_policy: "Policy / Legal",
    page_type_generic: "Generic",
    output_language: "Output language",
    speed_label: "Audit speed",
    store_lang_label: "Store language",
    store_lang_hint: "Force all LLM rewrites in this language. Any text in a different language inside the store will be flagged CRITICAL (Google treats mixed languages as a dropshipping signal). Choose 'Auto' to preserve each field's original language.",
    speed_fast: "Fast",
    speed_fast_sub: "~10s · Haiku 4.5",
    speed_fast_hint: "Faster scans (Claude Haiku 4.5). Recommended for batch and Shopify scans.",
    speed_accurate: "Accurate",
    speed_accurate_sub: "~22s · Sonnet 4.5",
    speed_accurate_hint: "Slower but more precise (Claude Sonnet 4.5). Use for critical single-page audits.",
    run_audit: "Run audit",
    run_batch_audit: "Run batch audit",
    running: "Auditing...",
    batch_running: "Auditing batch...",
    score: "Compliance score",
    summary: "Summary",
    violations: "Violations",
    no_violations: "No violations detected. Page appears compliant.",
    severity_critical: "Critical",
    severity_warning: "Warning",
    severity_info: "Info",
    original: "Original",
    suggested: "Compliant rewrite",
    copy: "Copy",
    copied: "Copied",
    delete: "Delete",
    deleted: "Deleted",
    stats_critical: "Critical",
    stats_warning: "Warnings",
    stats_info: "Info",
    stats_total: "Total",
    page_title: "Page title",
    meta_desc: "Meta description",
    detected_language: "Detected language",
    detected_language_signal: "signal",
    shopify_mixed_lang_count: "Mixed lang",
    shopify_mixed_lang_filter_on: "Showing mixed only",
    shopify_mixed_lang_filter_off: "Mixed lang only",
    shopify_mixed_lang_filter_hint: "Show only resources where at least one field mixes languages (Google's #1 dropshipping signal).",
    shopify_mixed_lang_filter_empty: "No resources with mixed languages — your store is consistent.",
    shopify_mixed_lang_autofix_button: "Translate mixed",
    shopify_mixed_lang_autofix_hint: "Apply the LLM rewrites in the store's primary language to all mixed-language resources in 1 click.",
    shopify_mixed_lang_autofix_nothing: "No mixed-language resources with applicable rewrites.",
    shopify_mixed_lang_autofix_confirm: "Translate {resources} resource(s) ({fields} fields) into the store's primary language? This will overwrite the current text on Shopify.",
    shopify_mixed_lang_autofix_done: "Mixed-language rewrites applied",
    source: "Source",
    created_at: "Date",
    audit_report: "Audit report",
    footer_note: "Independent tool. Not affiliated with Google.",
    export: "Export",
    export_csv: "CSV",
    export_pdf: "PDF",
    batch_results: "Batch results",
    average_score: "Avg. score",
    total_urls: "Total",
    succeeded: "Succeeded",
    failed: "Failed",
    batch_history: "Batches",
    no_batch_history: "No batches yet.",
    pages: "Pages",
    failed_urls: "Failed URLs",
    open: "Open",
    close: "Close",
  },
  fr: {
    app_name: "MerchantAudit",
    tagline: "Auditeur de conformité Google Merchant Center",
    history: "Historique d'audits",
    no_history: "Aucun audit pour le moment. Lancez votre premier scan.",
    input_mode_url: "URL",
    input_mode_text: "Texte brut",
    input_mode_batch: "Lot",
    batch_mode_sitemap: "Sitemap",
    batch_mode_urls: "Liste d'URLs",
    sitemap_url_placeholder: "https://votreboutique.com/sitemap.xml",
    urls_placeholder: "Une URL par ligne (max 50)...",
    max_urls: "URLs max",
    url_placeholder: "https://votreboutique.com/produit/123",
    text_placeholder: "Collez le texte de la page ici...",
    page_type: "Type de page",
    page_type_product: "Produit",
    page_type_category: "Catégorie",
    page_type_homepage: "Accueil",
    page_type_policy: "Politique / Légal",
    page_type_generic: "Générique",
    output_language: "Langue de sortie",
    speed_label: "Vitesse d'audit",
    store_lang_label: "Langue du store",
    store_lang_hint: "Force toutes les reformulations LLM dans cette langue. Tout texte dans une autre langue à l'intérieur du store sera marqué CRITIQUE (Google considère le mélange de langues comme un signal de dropshipping). Choisissez « Auto » pour préserver la langue d'origine de chaque champ.",
    speed_fast: "Rapide",
    speed_fast_sub: "~10s · Haiku 4.5",
    speed_fast_hint: "Scans plus rapides (Claude Haiku 4.5). Recommandé pour les batchs et scans Shopify.",
    speed_accurate: "Précis",
    speed_accurate_sub: "~22s · Sonnet 4.5",
    speed_accurate_hint: "Plus lent mais plus précis (Claude Sonnet 4.5). À utiliser pour les audits critiques d'une seule page.",
    run_audit: "Lancer l'audit",
    run_batch_audit: "Lancer l'audit groupé",
    running: "Analyse en cours...",
    batch_running: "Audit groupé en cours...",
    score: "Score de conformité",
    summary: "Résumé",
    violations: "Violations",
    no_violations: "Aucune violation détectée. La page semble conforme.",
    severity_critical: "Critique",
    severity_warning: "Avertissement",
    severity_info: "Info",
    original: "Original",
    suggested: "Reformulation conforme",
    copy: "Copier",
    copied: "Copié",
    delete: "Supprimer",
    deleted: "Supprimé",
    stats_critical: "Critiques",
    stats_warning: "Avertissements",
    stats_info: "Infos",
    stats_total: "Total",
    page_title: "Titre de page",
    meta_desc: "Meta description",
    detected_language: "Langue détectée",
    detected_language_signal: "signal",
    shopify_mixed_lang_count: "Langues mixtes",
    shopify_mixed_lang_filter_on: "Afficher uniquement mixtes",
    shopify_mixed_lang_filter_off: "Langues mixtes uniquement",
    shopify_mixed_lang_filter_hint: "Affiche uniquement les ressources dont au moins un champ contient un mélange de langues (signal #1 de dropshipping selon Google).",
    shopify_mixed_lang_filter_empty: "Aucune ressource avec un mélange de langues — votre boutique est cohérente.",
    shopify_mixed_lang_autofix_button: "Traduire mixtes",
    shopify_mixed_lang_autofix_hint: "Applique les reformulations LLM dans la langue principale du store sur toutes les ressources avec langue mixte, en 1 clic.",
    shopify_mixed_lang_autofix_nothing: "Aucune ressource mixte avec une reformulation applicable.",
    shopify_mixed_lang_autofix_confirm: "Traduire {resources} ressource(s) ({fields} champs) vers la langue principale du store ? Cela écrasera le texte actuel sur Shopify.",
    shopify_mixed_lang_autofix_done: "Reformulations multilingues appliquées",
    source: "Source",
    created_at: "Date",
    audit_report: "Rapport d'audit",
    footer_note: "Outil indépendant. Non affilié à Google.",
    export: "Exporter",
    export_csv: "CSV",
    export_pdf: "PDF",
    batch_results: "Résultats du lot",
    average_score: "Score moyen",
    total_urls: "Total",
    succeeded: "Réussis",
    failed: "Échoués",
    batch_history: "Lots",
    no_batch_history: "Aucun lot pour le moment.",
    pages: "Pages",
    failed_urls: "URLs en échec",
    open: "Ouvrir",
    close: "Fermer",
  },
  es: {
    app_name: "MerchantAudit",
    tagline: "Auditor de conformidad Google Merchant Center",
    history: "Historial",
    no_history: "Sin auditorías. Lanza tu primer escaneo.",
    input_mode_url: "URL",
    input_mode_text: "Texto",
    input_mode_batch: "Lote",
    batch_mode_sitemap: "Sitemap",
    batch_mode_urls: "Lista de URLs",
    sitemap_url_placeholder: "https://tutienda.com/sitemap.xml",
    urls_placeholder: "Una URL por línea (máx 50)...",
    max_urls: "URLs máx",
    url_placeholder: "https://tutienda.com/producto/123",
    text_placeholder: "Pega aquí el texto de la página...",
    page_type: "Tipo de página",
    page_type_product: "Producto",
    page_type_category: "Categoría",
    page_type_homepage: "Inicio",
    page_type_policy: "Política / Legal",
    page_type_generic: "Genérico",
    output_language: "Idioma de salida",
    speed_label: "Velocidad de auditoría",
    store_lang_label: "Idioma de la tienda",
    store_lang_hint: "Fuerza todas las reescrituras del LLM en este idioma. Cualquier texto en otro idioma dentro de la tienda se marcará como CRÍTICO (Google considera la mezcla de idiomas como una señal de dropshipping). Elija « Auto » para preservar el idioma original de cada campo.",
    speed_fast: "Rápido",
    speed_fast_sub: "~10s · Haiku 4.5",
    speed_fast_hint: "Escaneos más rápidos (Claude Haiku 4.5). Recomendado para lotes y escaneos de Shopify.",
    speed_accurate: "Preciso",
    speed_accurate_sub: "~22s · Sonnet 4.5",
    speed_accurate_hint: "Más lento pero más preciso (Claude Sonnet 4.5). Úselo para auditorías críticas de una sola página.",
    run_audit: "Lanzar auditoría",
    run_batch_audit: "Lanzar auditoría por lote",
    running: "Auditando...",
    batch_running: "Auditando lote...",
    score: "Puntuación de conformidad",
    summary: "Resumen",
    violations: "Violaciones",
    no_violations: "Sin violaciones detectadas.",
    severity_critical: "Crítico",
    severity_warning: "Aviso",
    severity_info: "Info",
    original: "Original",
    suggested: "Reescritura conforme",
    copy: "Copiar",
    copied: "Copiado",
    delete: "Eliminar",
    deleted: "Eliminado",
    stats_critical: "Críticas",
    stats_warning: "Avisos",
    stats_info: "Info",
    stats_total: "Total",
    page_title: "Título",
    meta_desc: "Meta descripción",
    detected_language: "Idioma detectado",
    detected_language_signal: "señal",
    shopify_mixed_lang_count: "Idiomas mezclados",
    shopify_mixed_lang_filter_on: "Mostrar solo mezclados",
    shopify_mixed_lang_filter_off: "Solo idiomas mezclados",
    shopify_mixed_lang_filter_hint: "Muestra solo los recursos donde al menos un campo mezcla idiomas (señal #1 de dropshipping según Google).",
    shopify_mixed_lang_filter_empty: "Ningún recurso con idiomas mezclados — su tienda es coherente.",
    shopify_mixed_lang_autofix_button: "Traducir mezclados",
    shopify_mixed_lang_autofix_hint: "Aplica las reescrituras del LLM en el idioma principal de la tienda a todos los recursos con idiomas mezclados, en 1 clic.",
    shopify_mixed_lang_autofix_nothing: "Ningún recurso mezclado con reescritura aplicable.",
    shopify_mixed_lang_autofix_confirm: "¿Traducir {resources} recurso(s) ({fields} campos) al idioma principal de la tienda? Esto sobrescribirá el texto actual en Shopify.",
    shopify_mixed_lang_autofix_done: "Reescrituras multilingües aplicadas",
    source: "Fuente",
    created_at: "Fecha",
    audit_report: "Informe de auditoría",
    footer_note: "Herramienta independiente. No afiliada a Google.",
    export: "Exportar",
    export_csv: "CSV",
    export_pdf: "PDF",
    batch_results: "Resultados del lote",
    average_score: "Puntuación media",
    total_urls: "Total",
    succeeded: "Exitosos",
    failed: "Fallidos",
    batch_history: "Lotes",
    no_batch_history: "Sin lotes.",
    pages: "Páginas",
    failed_urls: "URLs fallidas",
    open: "Abrir",
    close: "Cerrar",
  },
  it: {
    app_name: "MerchantAudit",
    tagline: "Auditor di conformità Google Merchant Center",
    history: "Cronologia",
    no_history: "Nessun audit. Avvia la prima scansione.",
    input_mode_url: "URL",
    input_mode_text: "Testo",
    input_mode_batch: "Batch",
    batch_mode_sitemap: "Sitemap",
    batch_mode_urls: "Elenco URL",
    sitemap_url_placeholder: "https://iltuonegozio.com/sitemap.xml",
    urls_placeholder: "Un URL per riga (max 50)...",
    max_urls: "URL max",
    url_placeholder: "https://iltuonegozio.com/prodotto/123",
    text_placeholder: "Incolla qui il testo della pagina...",
    page_type: "Tipo di pagina",
    page_type_product: "Prodotto",
    page_type_category: "Categoria",
    page_type_homepage: "Home",
    page_type_policy: "Policy / Legale",
    page_type_generic: "Generico",
    output_language: "Lingua di output",
    speed_label: "Velocità audit",
    store_lang_label: "Lingua del negozio",
    store_lang_hint: "Forza tutte le riformulazioni LLM in questa lingua. Qualsiasi testo in un'altra lingua all'interno del negozio sarà segnalato come CRITICO (Google considera la mescolanza di lingue come un segnale di dropshipping). Scegli « Auto » per preservare la lingua originale di ciascun campo.",
    speed_fast: "Veloce",
    speed_fast_sub: "~10s · Haiku 4.5",
    speed_fast_hint: "Scansioni più veloci (Claude Haiku 4.5). Consigliato per batch e scansioni Shopify.",
    speed_accurate: "Preciso",
    speed_accurate_sub: "~22s · Sonnet 4.5",
    speed_accurate_hint: "Più lento ma più preciso (Claude Sonnet 4.5). Da usare per audit critici di una singola pagina.",
    run_audit: "Avvia audit",
    run_batch_audit: "Avvia audit batch",
    running: "Analisi in corso...",
    batch_running: "Audit batch in corso...",
    score: "Punteggio di conformità",
    summary: "Riepilogo",
    violations: "Violazioni",
    no_violations: "Nessuna violazione rilevata.",
    severity_critical: "Critico",
    severity_warning: "Avviso",
    severity_info: "Info",
    original: "Originale",
    suggested: "Riformulazione conforme",
    copy: "Copia",
    copied: "Copiato",
    delete: "Elimina",
    deleted: "Eliminato",
    stats_critical: "Critiche",
    stats_warning: "Avvisi",
    stats_info: "Info",
    stats_total: "Totale",
    page_title: "Titolo",
    meta_desc: "Meta descrizione",
    detected_language: "Lingua rilevata",
    detected_language_signal: "segnale",
    shopify_mixed_lang_count: "Lingue miste",
    shopify_mixed_lang_filter_on: "Mostra solo miste",
    shopify_mixed_lang_filter_off: "Solo lingue miste",
    shopify_mixed_lang_filter_hint: "Mostra solo le risorse in cui almeno un campo mescola lingue (segnale #1 di dropshipping secondo Google).",
    shopify_mixed_lang_filter_empty: "Nessuna risorsa con lingue miste — il tuo negozio è coerente.",
    shopify_mixed_lang_autofix_button: "Traduci miste",
    shopify_mixed_lang_autofix_hint: "Applica le riscritture LLM nella lingua principale del negozio a tutte le risorse con lingue miste, in 1 clic.",
    shopify_mixed_lang_autofix_nothing: "Nessuna risorsa mista con una riscrittura applicabile.",
    shopify_mixed_lang_autofix_confirm: "Tradurre {resources} risorsa(e) ({fields} campi) nella lingua principale del negozio? Ciò sovrascriverà il testo attuale su Shopify.",
    shopify_mixed_lang_autofix_done: "Riscritture multilingue applicate",
    source: "Fonte",
    created_at: "Data",
    audit_report: "Report di audit",
    footer_note: "Strumento indipendente. Non affiliato a Google.",
    export: "Esporta",
    export_csv: "CSV",
    export_pdf: "PDF",
    batch_results: "Risultati batch",
    average_score: "Punteggio medio",
    total_urls: "Totale",
    succeeded: "Riusciti",
    failed: "Falliti",
    batch_history: "Batch",
    no_batch_history: "Nessun batch.",
    pages: "Pagine",
    failed_urls: "URL falliti",
    open: "Apri",
    close: "Chiudi",
  },
  pt: {
    app_name: "MerchantAudit",
    tagline: "Auditor de conformidade Google Merchant Center",
    history: "Histórico",
    no_history: "Sem auditorias. Inicie o primeiro scan.",
    input_mode_url: "URL",
    input_mode_text: "Texto",
    input_mode_batch: "Lote",
    batch_mode_sitemap: "Sitemap",
    batch_mode_urls: "Lista de URLs",
    sitemap_url_placeholder: "https://sualoja.com/sitemap.xml",
    urls_placeholder: "Um URL por linha (máx 50)...",
    max_urls: "URLs máx",
    url_placeholder: "https://sualoja.com/produto/123",
    text_placeholder: "Cole aqui o texto da página...",
    page_type: "Tipo de página",
    page_type_product: "Produto",
    page_type_category: "Categoria",
    page_type_homepage: "Início",
    page_type_policy: "Política / Legal",
    page_type_generic: "Genérico",
    output_language: "Idioma de saída",
    speed_label: "Velocidade da auditoria",
    store_lang_label: "Idioma da loja",
    store_lang_hint: "Força todas as reescritas do LLM neste idioma. Qualquer texto noutro idioma dentro da loja será sinalizado como CRÍTICO (o Google trata a mistura de idiomas como sinal de dropshipping). Escolha « Auto » para preservar o idioma original de cada campo.",
    speed_fast: "Rápido",
    speed_fast_sub: "~10s · Haiku 4.5",
    speed_fast_hint: "Verificações mais rápidas (Claude Haiku 4.5). Recomendado para lotes e verificações da Shopify.",
    speed_accurate: "Preciso",
    speed_accurate_sub: "~22s · Sonnet 4.5",
    speed_accurate_hint: "Mais lento mas mais preciso (Claude Sonnet 4.5). Use para auditorias críticas de uma única página.",
    run_audit: "Iniciar auditoria",
    run_batch_audit: "Iniciar auditoria em lote",
    running: "Analisando...",
    batch_running: "Auditando lote...",
    score: "Pontuação de conformidade",
    summary: "Resumo",
    violations: "Violações",
    no_violations: "Nenhuma violação detectada.",
    severity_critical: "Crítico",
    severity_warning: "Aviso",
    severity_info: "Info",
    original: "Original",
    suggested: "Reescrita conforme",
    copy: "Copiar",
    copied: "Copiado",
    delete: "Excluir",
    deleted: "Excluído",
    stats_critical: "Críticos",
    stats_warning: "Avisos",
    stats_info: "Info",
    stats_total: "Total",
    page_title: "Título",
    meta_desc: "Meta descrição",
    detected_language: "Idioma detectado",
    detected_language_signal: "sinal",
    shopify_mixed_lang_count: "Idiomas misturados",
    shopify_mixed_lang_filter_on: "Mostrar apenas misturados",
    shopify_mixed_lang_filter_off: "Só idiomas misturados",
    shopify_mixed_lang_filter_hint: "Mostra apenas recursos onde pelo menos um campo mistura idiomas (sinal #1 de dropshipping segundo o Google).",
    shopify_mixed_lang_filter_empty: "Nenhum recurso com idiomas misturados — a sua loja é coerente.",
    shopify_mixed_lang_autofix_button: "Traduzir misturados",
    shopify_mixed_lang_autofix_hint: "Aplica as reescritas do LLM no idioma principal da loja a todos os recursos com idiomas misturados, em 1 clique.",
    shopify_mixed_lang_autofix_nothing: "Nenhum recurso misturado com reescrita aplicável.",
    shopify_mixed_lang_autofix_confirm: "Traduzir {resources} recurso(s) ({fields} campos) para o idioma principal da loja? Isso substituirá o texto atual no Shopify.",
    shopify_mixed_lang_autofix_done: "Reescritas multilingues aplicadas",
    source: "Fonte",
    created_at: "Data",
    audit_report: "Relatório de auditoria",
    footer_note: "Ferramenta independente. Não afiliada ao Google.",
    export: "Exportar",
    export_csv: "CSV",
    export_pdf: "PDF",
    batch_results: "Resultados do lote",
    average_score: "Pontuação média",
    total_urls: "Total",
    succeeded: "Sucesso",
    failed: "Falhas",
    batch_history: "Lotes",
    no_batch_history: "Sem lotes.",
    pages: "Páginas",
    failed_urls: "URLs com falha",
    open: "Abrir",
    close: "Fechar",
  },
  de: {
    app_name: "MerchantAudit",
    tagline: "Compliance-Audit für Google Merchant Center",
    history: "Verlauf",
    no_history: "Keine Prüfungen. Starten Sie Ihren ersten Scan.",
    input_mode_url: "URL",
    input_mode_text: "Text",
    input_mode_batch: "Batch",
    batch_mode_sitemap: "Sitemap",
    batch_mode_urls: "URL-Liste",
    sitemap_url_placeholder: "https://ihrshop.de/sitemap.xml",
    urls_placeholder: "Ein URL pro Zeile (max 50)...",
    max_urls: "Max URLs",
    url_placeholder: "https://ihrshop.de/produkt/123",
    text_placeholder: "Seitentext hier einfügen...",
    page_type: "Seitentyp",
    page_type_product: "Produkt",
    page_type_category: "Kategorie",
    page_type_homepage: "Startseite",
    page_type_policy: "Richtlinie / Rechtliches",
    page_type_generic: "Allgemein",
    output_language: "Ausgabesprache",
    speed_label: "Audit-Geschwindigkeit",
    store_lang_label: "Sprache des Stores",
    store_lang_hint: "Erzwingt alle LLM-Umformulierungen in dieser Sprache. Jeder Text in einer anderen Sprache im Store wird als KRITISCH markiert (Google wertet Sprachmischung als Dropshipping-Signal). Wählen Sie « Auto », um die Originalsprache jedes Felds beizubehalten.",
    speed_fast: "Schnell",
    speed_fast_sub: "~10s · Haiku 4.5",
    speed_fast_hint: "Schnellere Scans (Claude Haiku 4.5). Empfohlen für Batches und Shopify-Scans.",
    speed_accurate: "Präzise",
    speed_accurate_sub: "~22s · Sonnet 4.5",
    speed_accurate_hint: "Langsamer, aber genauer (Claude Sonnet 4.5). Für kritische Einzelseiten-Audits verwenden.",
    run_audit: "Prüfung starten",
    run_batch_audit: "Batch-Prüfung starten",
    running: "Wird geprüft...",
    batch_running: "Batch wird geprüft...",
    score: "Compliance-Score",
    summary: "Zusammenfassung",
    violations: "Verstöße",
    no_violations: "Keine Verstöße festgestellt.",
    severity_critical: "Kritisch",
    severity_warning: "Warnung",
    severity_info: "Info",
    original: "Original",
    suggested: "Konforme Umformulierung",
    copy: "Kopieren",
    copied: "Kopiert",
    delete: "Löschen",
    deleted: "Gelöscht",
    stats_critical: "Kritisch",
    stats_warning: "Warnungen",
    stats_info: "Info",
    stats_total: "Gesamt",
    page_title: "Seitentitel",
    meta_desc: "Meta-Beschreibung",
    detected_language: "Erkannte Sprache",
    detected_language_signal: "Signal",
    shopify_mixed_lang_count: "Sprachmischung",
    shopify_mixed_lang_filter_on: "Nur gemischte anzeigen",
    shopify_mixed_lang_filter_off: "Nur Sprachmischung",
    shopify_mixed_lang_filter_hint: "Zeigt nur Ressourcen, bei denen mindestens ein Feld Sprachen mischt (Googles #1 Dropshipping-Signal).",
    shopify_mixed_lang_filter_empty: "Keine Ressourcen mit Sprachmischung — Ihr Store ist konsistent.",
    shopify_mixed_lang_autofix_button: "Mixe übersetzen",
    shopify_mixed_lang_autofix_hint: "Wendet die LLM-Umformulierungen in der Hauptsprache des Stores auf alle Ressourcen mit Sprachmischung an, mit 1 Klick.",
    shopify_mixed_lang_autofix_nothing: "Keine Ressourcen mit Sprachmischung und anwendbarer Umformulierung.",
    shopify_mixed_lang_autofix_confirm: "{resources} Ressource(n) ({fields} Felder) in die Hauptsprache des Stores übersetzen? Dies überschreibt den aktuellen Text auf Shopify.",
    shopify_mixed_lang_autofix_done: "Mehrsprachige Umformulierungen angewendet",
    source: "Quelle",
    created_at: "Datum",
    audit_report: "Prüfbericht",
    footer_note: "Unabhängiges Tool. Nicht mit Google verbunden.",
    export: "Exportieren",
    export_csv: "CSV",
    export_pdf: "PDF",
    batch_results: "Batch-Ergebnisse",
    average_score: "Ø Score",
    total_urls: "Gesamt",
    succeeded: "Erfolgreich",
    failed: "Fehlgeschlagen",
    batch_history: "Batches",
    no_batch_history: "Keine Batches.",
    pages: "Seiten",
    failed_urls: "Fehlgeschlagene URLs",
    open: "Öffnen",
    close: "Schließen",
  },
};

export const translations = KEYS;

// Shopify-specific keys (appended to each language). Keeping them in one place for clarity.
const SHOPIFY_KEYS = {
  en: {
    shopify_tab: "Shopify",
    compliance_tab: "Compliance",
    audit_tab: "Audit",
    shopify_title: "Shopify connections",
    shopify_no_conn: "No connection yet. Add one to scan your store.",
    shopify_add_conn: "Add a connection",
    shopify_name: "Name",
    shopify_name_ph: "My store (EU)",
    shopify_store_url: "Store URL",
    shopify_store_ph: "mystore.myshopify.com",
    shopify_token: "Admin API access token",
    shopify_token_ph: "shpat_...",
    shopify_token_help: "Create a Custom app in Shopify admin → Apps → Develop apps. Scopes: read_products, write_products, read_content, write_content, read_themes, write_themes, read_online_store_navigation, write_online_store_navigation.",
    shopify_connect: "Connect",
    shopify_connected: "Connected",
    shopify_test: "Test",
    shopify_test_ok: "Connection OK",
    shopify_scan: "Scan store",
    shopify_scanning: "Scanning...",
    shopify_resources: "Resources to scan",
    shopify_max_per: "Max per resource",
    res_product: "Products",
    res_page: "Pages",
    res_custom_collection: "Collections (custom)",
    res_smart_collection: "Collections (smart)",
    res_policy: "Policies (read-only)",
    shopify_scan_results: "Scan results",
    shopify_apply_selected: "Apply selected to Shopify",
    shopify_applying: "Applying...",
    shopify_applied: "Applied to Shopify",
    shopify_fields_title: "Title",
    shopify_fields_body: "Description",
    shopify_fields_seo_title: "SEO title",
    shopify_fields_seo_desc: "SEO description",
    shopify_readonly_note: "Read-only via Admin API. Copy the rewrite manually into Shopify admin.",
    shopify_no_change: "Already compliant",
    shopify_select_all: "Select all",
    shopify_avg: "Avg. score",
    shopify_compliant: "Compliant",
    shopify_confirm_apply: "This will overwrite the selected fields in your Shopify store. Continue?",
  },
  fr: {
    compliance_tab: "Compliance",
    shopify_tab: "Shopify",
    audit_tab: "Audit",
    shopify_title: "Connexions Shopify",
    shopify_no_conn: "Aucune connexion. Ajoutez-en une pour scanner votre boutique.",
    shopify_add_conn: "Ajouter une connexion",
    shopify_name: "Nom",
    shopify_name_ph: "Ma boutique (UE)",
    shopify_store_url: "URL boutique",
    shopify_store_ph: "maboutique.myshopify.com",
    shopify_token: "Admin API access token",
    shopify_token_ph: "shpat_...",
    shopify_token_help: "Créez une Custom app dans Shopify admin → Apps → Développer des apps. Scopes : read_products, write_products, read_content, write_content, read_themes, write_themes, read_online_store_navigation, write_online_store_navigation.",
    shopify_connect: "Connecter",
    shopify_connected: "Connecté",
    shopify_test: "Tester",
    shopify_test_ok: "Connexion OK",
    shopify_scan: "Scanner la boutique",
    shopify_scanning: "Scan en cours...",
    shopify_resources: "Ressources à scanner",
    shopify_max_per: "Max par type",
    res_product: "Produits",
    res_page: "Pages",
    res_custom_collection: "Catégories (custom)",
    res_smart_collection: "Catégories (smart)",
    res_policy: "Politiques (lecture seule)",
    shopify_scan_results: "Résultats du scan",
    shopify_apply_selected: "Appliquer à Shopify",
    shopify_applying: "Application...",
    shopify_applied: "Appliqué à Shopify",
    shopify_fields_title: "Titre",
    shopify_fields_body: "Description",
    shopify_fields_seo_title: "Titre SEO",
    shopify_fields_seo_desc: "Description SEO",
    shopify_readonly_note: "Lecture seule via Admin API. Copiez la reformulation manuellement dans Shopify.",
    shopify_no_change: "Déjà conforme",
    shopify_select_all: "Tout sélectionner",
    shopify_avg: "Score moyen",
    shopify_compliant: "Conformes",
    shopify_confirm_apply: "Ceci va écraser les champs sélectionnés dans votre boutique Shopify. Continuer ?",
  },
    compliance_tab: "Compliance",
  es: {
    shopify_tab: "Shopify",
    audit_tab: "Auditoría",
    shopify_title: "Conexiones Shopify",
    shopify_no_conn: "Sin conexión. Añada una para escanear su tienda.",
    shopify_add_conn: "Añadir conexión",
    shopify_name: "Nombre",
    shopify_name_ph: "Mi tienda (UE)",
    shopify_store_url: "URL de la tienda",
    shopify_store_ph: "mitienda.myshopify.com",
    shopify_token: "Admin API access token",
    shopify_token_ph: "shpat_...",
    shopify_token_help: "Cree una Custom app en Shopify admin → Apps → Develop apps. Scopes: read_products, write_products, read_content, write_content, read_themes, write_themes, read_online_store_navigation, write_online_store_navigation.",
    shopify_connect: "Conectar",
    shopify_connected: "Conectado",
    shopify_test: "Probar",
    shopify_test_ok: "Conexión OK",
    shopify_scan: "Escanear tienda",
    shopify_scanning: "Escaneando...",
    shopify_resources: "Recursos a escanear",
    shopify_max_per: "Máx por tipo",
    res_product: "Productos",
    res_page: "Páginas",
    res_custom_collection: "Categorías (custom)",
    res_smart_collection: "Categorías (smart)",
    res_policy: "Políticas (solo lectura)",
    shopify_scan_results: "Resultados del escaneo",
    shopify_apply_selected: "Aplicar en Shopify",
    shopify_applying: "Aplicando...",
    shopify_applied: "Aplicado en Shopify",
    shopify_fields_title: "Título",
    shopify_fields_body: "Descripción",
    shopify_fields_seo_title: "Título SEO",
    shopify_fields_seo_desc: "Descripción SEO",
    shopify_readonly_note: "Solo lectura vía Admin API. Copie la reescritura manualmente.",
    shopify_no_change: "Ya cumple",
    shopify_select_all: "Seleccionar todo",
    shopify_avg: "Puntuación media",
    shopify_compliant: "Conformes",
    shopify_confirm_apply: "Esto sobrescribirá los campos seleccionados en Shopify. ¿Continuar?",
    compliance_tab: "Compliance",
  },
  it: {
    shopify_tab: "Shopify",
    audit_tab: "Audit",
    shopify_title: "Connessioni Shopify",
    shopify_no_conn: "Nessuna connessione. Aggiungila per scansionare il negozio.",
    shopify_add_conn: "Aggiungi connessione",
    shopify_name: "Nome",
    shopify_name_ph: "Il mio negozio (UE)",
    shopify_store_url: "URL negozio",
    shopify_store_ph: "ilmionegozio.myshopify.com",
    shopify_token: "Admin API access token",
    shopify_token_ph: "shpat_...",
    shopify_token_help: "Crea una Custom app in Shopify admin → Apps → Develop apps. Scopes: read_products, write_products, read_content, write_content, read_themes, write_themes, read_online_store_navigation, write_online_store_navigation.",
    shopify_connect: "Connetti",
    shopify_connected: "Connesso",
    shopify_test: "Prova",
    shopify_test_ok: "Connessione OK",
    shopify_scan: "Scansiona negozio",
    shopify_scanning: "Scansione...",
    shopify_resources: "Risorse da scansionare",
    shopify_max_per: "Max per tipo",
    res_product: "Prodotti",
    res_page: "Pagine",
    res_custom_collection: "Categorie (custom)",
    res_smart_collection: "Categorie (smart)",
    res_policy: "Policy (sola lettura)",
    shopify_scan_results: "Risultati scansione",
    shopify_apply_selected: "Applica a Shopify",
    shopify_applying: "Applicazione...",
    shopify_applied: "Applicato a Shopify",
    shopify_fields_title: "Titolo",
    shopify_fields_body: "Descrizione",
    shopify_fields_seo_title: "Titolo SEO",
    shopify_fields_seo_desc: "Descrizione SEO",
    shopify_readonly_note: "Sola lettura via Admin API. Copia manualmente la riformulazione.",
    shopify_no_change: "Già conforme",
    shopify_select_all: "Seleziona tutto",
    shopify_avg: "Punteggio medio",
    shopify_compliant: "Conformi",
    compliance_tab: "Compliance",
    shopify_confirm_apply: "Questo sovrascriverà i campi selezionati su Shopify. Continuare?",
  },
  pt: {
    shopify_tab: "Shopify",
    audit_tab: "Auditoria",
    shopify_title: "Conexões Shopify",
    shopify_no_conn: "Sem conexão. Adicione uma para escanear sua loja.",
    shopify_add_conn: "Adicionar conexão",
    shopify_name: "Nome",
    shopify_name_ph: "Minha loja (UE)",
    shopify_store_url: "URL da loja",
    shopify_store_ph: "minhaloja.myshopify.com",
    shopify_token: "Admin API access token",
    shopify_token_ph: "shpat_...",
    shopify_token_help: "Crie uma Custom app em Shopify admin → Apps → Develop apps. Scopes: read_products, write_products, read_content, write_content, read_themes, write_themes, read_online_store_navigation, write_online_store_navigation.",
    shopify_connect: "Conectar",
    shopify_connected: "Conectado",
    shopify_test: "Testar",
    shopify_test_ok: "Conexão OK",
    shopify_scan: "Escanear loja",
    shopify_scanning: "Escaneando...",
    shopify_resources: "Recursos a escanear",
    shopify_max_per: "Máx por tipo",
    res_product: "Produtos",
    res_page: "Páginas",
    res_custom_collection: "Categorias (custom)",
    res_smart_collection: "Categorias (smart)",
    res_policy: "Políticas (somente leitura)",
    shopify_scan_results: "Resultados do scan",
    shopify_apply_selected: "Aplicar no Shopify",
    shopify_applying: "Aplicando...",
    shopify_applied: "Aplicado no Shopify",
    shopify_fields_title: "Título",
    shopify_fields_body: "Descrição",
    shopify_fields_seo_title: "Título SEO",
    shopify_fields_seo_desc: "Descrição SEO",
    shopify_readonly_note: "Somente leitura via Admin API. Copie manualmente a reescrita.",
    shopify_no_change: "Já conforme",
    shopify_select_all: "Selecionar tudo",
    shopify_avg: "Pontuação média",
    compliance_tab: "Compliance",
    shopify_compliant: "Conformes",
    shopify_confirm_apply: "Isso sobrescreverá os campos selecionados no Shopify. Continuar?",
  },
  de: {
    shopify_tab: "Shopify",
    audit_tab: "Audit",
    shopify_title: "Shopify-Verbindungen",
    shopify_no_conn: "Noch keine Verbindung. Fügen Sie eine hinzu, um Ihren Shop zu scannen.",
    shopify_add_conn: "Verbindung hinzufügen",
    shopify_name: "Name",
    shopify_name_ph: "Mein Shop (EU)",
    shopify_store_url: "Shop-URL",
    shopify_store_ph: "meinshop.myshopify.com",
    shopify_token: "Admin API access token",
    shopify_token_ph: "shpat_...",
    shopify_token_help: "Erstellen Sie eine Custom App unter Shopify Admin → Apps → Apps entwickeln. Scopes: read_products, write_products, read_content, write_content, read_themes, write_themes, read_online_store_navigation, write_online_store_navigation.",
    shopify_connect: "Verbinden",
    shopify_connected: "Verbunden",
    shopify_test: "Testen",
    shopify_test_ok: "Verbindung OK",
    shopify_scan: "Shop scannen",
    shopify_scanning: "Scan läuft...",
    shopify_resources: "Zu scannende Ressourcen",
    shopify_max_per: "Max pro Typ",
    res_product: "Produkte",
    res_page: "Seiten",
    res_custom_collection: "Kategorien (custom)",
    res_smart_collection: "Kategorien (smart)",
    res_policy: "Richtlinien (nur lesen)",
    shopify_scan_results: "Scan-Ergebnisse",
    shopify_apply_selected: "Auf Shopify anwenden",
    shopify_applying: "Anwenden...",
    shopify_applied: "Auf Shopify angewendet",
    shopify_fields_title: "Titel",
    shopify_fields_body: "Beschreibung",
    shopify_fields_seo_title: "SEO-Titel",
    shopify_fields_seo_desc: "SEO-Beschreibung",
    shopify_readonly_note: "Nur lesen via Admin API. Umformulierung manuell einfügen.",
    shopify_no_change: "Bereits konform",
    shopify_select_all: "Alle auswählen",
    shopify_avg: "Ø Score",
    shopify_compliant: "Konform",
    shopify_confirm_apply: "Die ausgewählten Felder werden in Shopify überschrieben. Fortfahren?",
  },
};

// Merge Shopify keys into each language
Object.keys(SHOPIFY_KEYS).forEach((lng) => {
  KEYS[lng] = { ...KEYS[lng], ...SHOPIFY_KEYS[lng] };
});

// Rejection helper keys (appended to each language)
const REJECTION_KEYS = {
  en: {
    rejection_tab: "Rejection",
    rejection_title: "Google Merchant rejection resolver",
    rejection_email_label: "Paste Google's rejection email",
    rejection_email_ph: "Paste here the full email you received from Google Merchant Center...",
    rejection_email_required: "Please paste the Google rejection email",
    rejection_product_url: "Rejected product URL",
    rejection_product_text: "Rejected product copy",
    rejection_product_text_ph: "Paste here the title + description of the rejected product...",
    rejection_product_type: "Product type (optional)",
    rejection_product_type_ph: "e.g. jewelry, cosmetics, supplements...",
    rejection_run: "Diagnose & fix",
    rejection_running: "Analyzing...",
    rejection_rewrites: "Issues found & rewrites",
    rejection_no_issues: "No clear triggering phrases detected. Double-check your full catalog or contact Google support.",
    rejection_appeal_title: "Appeal email to Google",
    rejection_checklist: "Google's 8 Misrepresentation sub-categories",
    rejection_checklist_intro: "Cross-check your full catalog against each of these:",
    rejection_policy_link: "Read the official Google policy",
    rejection_cat_1: "Unavailable promotions or offers (fake giveaways, missing stock)",
    rejection_cat_2: "Inaccurate prices or fees (price mismatch, hidden shipping/taxes)",
    rejection_cat_3: "Misleading endorsements or affiliations (fake reviews, fake awards, unverified expert claims)",
    rejection_cat_4: "False or misleading product claims (miracle cures, absolute superlatives, unverified \"100% authentic\")",
    rejection_cat_5: "Misleading discounts (fake crossed-out prices, fake -80%, broken promo codes)",
    rejection_cat_6: "Misleading availability / scarcity (\"only 2 left\", fake urgency, fake countdowns)",
    rejection_cat_7: "Hidden or unclear payment / shipping / return terms",
    rejection_cat_8: "Hidden or misrepresented business identity (missing company name, address, contact)",
    cancel: "Cancel",
    apply_modal_title: "Apply fix to Shopify",
    apply_modal_snippet: "Rewrite to push",
    apply_modal_connection: "Shopify store",
    apply_modal_resource_type: "Resource type",
    apply_modal_search: "Find resource",
    apply_modal_search_ph: "Search by title…",
    apply_modal_no_results: "No resources found. Adjust your search.",
    apply_modal_field: "Target field",
    apply_modal_field_hint: "We'll find the original phrase inside this field and replace it with the suggested one. The rest of the content is preserved.",
    apply_modal_applying: "Pushing…",
    apply_modal_submit: "Apply to Shopify",
    apply_no_conn: "No Shopify store connected. Connect one from the Shopify tab first.",
    apply_phrase_not_found: "Original phrase was not found in the live Shopify field. Edit it manually or pick a different resource/field.",
    diff_inline: "Inline diff",
    diff_inline_hint: "Word-level highlighting of removed/added text in a single block",
    diff_inline_title: "Compliant rewrite — inline diff",
    diff_side: "Side-by-side",
    diff_side_hint: "Show original and suggested rewrites in two separate columns",
    diff_removed: "removed",
    diff_added: "added",
    product_picker_button: "Pick products",
    product_picker_hint: "Choose a specific subset of products to scan (instead of the first N).",
    product_picker_title: "Pick products to scan",
    product_picker_loaded: "loaded",
    product_picker_selected: "selected",
    product_picker_filter_placeholder: "Filter by title…",
    product_picker_select_visible: "Select visible",
    product_picker_unselect_visible: "Unselect visible",
    product_picker_clear: "Clear all",
    product_picker_loading: "Loading products…",
    product_picker_no_match: "No products match this filter on this page.",
    product_picker_empty: "No products on this page.",
    product_picker_prev: "Prev",
    product_picker_next: "Next page",
    product_picker_page: "Page",
    product_picker_confirm: "Use selection",
    product_picker_picked_count: "Selection",
    product_picker_reset_hint: "Clear selection (scan all again)",
    product_picker_reset_toast: "Selection cleared — scan will use the default limit.",
    product_picker_picked_toast: "Products picked",
    product_picker_filter_hint: "Filter products by past audit score",
    product_picker_filter_all: "All products",
    product_picker_filter_never: "Never scanned",
    product_picker_filter_lt90: "Score < 90",
    product_picker_filter_lt70: "Score < 70",
    product_picker_never_scanned: "Never scanned",
    product_picker_last_scanned: "Last scanned",
    auto_route_title: "Auto-route — apply to wherever the phrase exists",
    auto_route_hint: "Scans products, pages, collections, policies and theme settings; applies each rewrite where the original phrase is actually found. Recommended.",
    auto_route_or_manual: "Or pick a specific resource manually below",
    auto_route_button: "Auto-route & Apply",
    auto_route_running: "Scanning store…",
    auto_route_applied: "Applied",
    auto_route_not_found: "Not found",
    auto_route_errors: "Errors",
    auto_route_not_found_one: "Not found anywhere in the store.",
    auto_route_none_found: "None of the original phrases were found across the store.",
    auto_route_partial_or_error: "Auto-route completed with some skipped or errored items — see breakdown.",
    auto_route_dry_run_button: "Preview only",
    auto_route_dry_run_hint: "Search the store for matching phrases without writing anything to Shopify. Lets you verify what will be touched before applying.",
    auto_route_dry_run_done: "Dry run complete — matches found",
    auto_route_dry_run_badge: "Dry run — nothing was changed",
    auto_route_dry_run_matches: "Would be applied to",
    apply_success: "Fix applied to Shopify",
    apply_updated_fields: "Updated fields",
    apply_warn_title: "Nothing applied",
    apply_done_title: "Fix applied",
    apply_to_shopify_btn: "Apply to Shopify",
    apply_replacements_count: "Phrases replaced",
    shopify_help_auto_title: "How to connect automatically (OAuth)",
    shopify_help_auto_step1: "Type your full Shopify store URL — example: mystore.myshopify.com (no https://, no path)",
    shopify_help_auto_step2: "Click \"Install with Shopify\" — a new tab opens on Shopify's authorization screen",
    shopify_help_auto_step3: "Log into Shopify (if not already) and confirm by clicking \"Install app\"",
    shopify_help_auto_step4: "Shopify will redirect you back to MerchantAudit. The connection appears at the top of this list — done.",
    shopify_help_auto_troubleshoot: "If Shopify returns \"redirect_uri is not whitelisted\" or you stay stuck on Shopify after Install: the OAuth app is not configured for THIS domain. Use Manual Custom App below as a workaround, or update APP_PUBLIC_URL in your production environment and the redirect URI in your Shopify Partners app.",
    shopify_help_manual_title: "How to connect manually (Custom App)",
    shopify_help_manual_step1: "Open your Shopify Admin → Settings (bottom left) → Apps and sales channels",
    shopify_help_manual_step2: "Click \"Develop apps\". If the button is missing, click \"Allow custom app development\" first, then refresh.",
    shopify_help_manual_step3: "Click \"Create an app\" → choose a name (e.g. \"MerchantAudit\") → \"Create app\"",
    shopify_help_manual_step4: "Open the \"Configuration\" tab → \"Admin API access scopes\" → \"Configure\" → enable: read_products, write_products, read_content, write_content, read_themes, write_themes, read_online_store_navigation, write_online_store_navigation. Save.",
    shopify_help_manual_step5: "Open the \"API credentials\" tab → click \"Install app\" → confirm \"Install\"",
    shopify_help_manual_step6: "Copy the \"Admin API access token\" (starts with shpat_…) — it is shown ONLY ONCE, save it now.",
    shopify_help_manual_step7: "Fill the three fields below: any nickname for the connection, your store URL (xxx.myshopify.com), the access token. Click \"Connect\".",
    shopify_help_manual_troubleshoot: "If you get \"Invalid API key or access token\": your token is wrong or you forgot the \"Install app\" step. If you get \"store_url must end with .myshopify.com\": type the URL without https:// and without trailing /.",
    shopify_token_warning_title: "Don't use Hoppscotch / Postman for OAuth",
    shopify_token_warning_body: "The token starting with shpat_… is shown DIRECTLY in your Shopify Admin, on the \"API credentials\" tab AFTER you click \"Install app\". You do NOT need to do a manual OAuth exchange with client_id + client_secret. If your token starts with shpss_… that is the client secret, NOT the access token.",
    shopify_health_ok: "All systems green — OAuth + manual + theme audit ready",
    shopify_health_warn: "Mostly OK — minor configuration issue",
    shopify_health_manual_only: "OAuth not configured — manual Custom App connection only",
    shopify_health_broken: "Configuration incomplete — Shopify connection will fail",
    shopify_health_oauth_configured: "OAuth keys configured",
    shopify_health_host_match: "App URL matches this domain",
    shopify_health_encryption: "Token encryption ready",
    shopify_health_scope_read_themes: "Scope read_themes granted",
    shopify_health_redirect_uri: "Callback URL to whitelist",
    shopify_health_warn_hint: "Add 'read_themes' to SHOPIFY_APP_SCOPES env var and redeploy to unlock theme audit.",
    shopify_health_manual_only_hint: "Set SHOPIFY_API_KEY + SHOPIFY_API_SECRET in your env to enable 1-click OAuth install. In the meantime, use the Manual Custom App flow below.",
    shopify_health_broken_hint: "Fix the red items above and redeploy. Most likely cause: APP_PUBLIC_URL does not match the domain you're using right now.",
    shopify_theme_apply_one: "Apply",
    shopify_theme_apply_all: "Apply all",
    shopify_theme_applied: "Applied",
    shopify_theme_apply_done: "Fixes pushed to theme",
    shopify_theme_no_apply: "No fix applied (paths did not match the live theme).",
    shopify_theme_apply_warn_title: "Theme write-back enabled.",
    shopify_theme_apply_warn_hint: "Each Apply edits config/settings_data.json of the published theme directly. Preview your shop after applying.",
    shopify_theme_autopilot: "Auto-pilot theme",
    shopify_theme_autopilot_hint: "Apply ALL theme findings matching the chosen minimum severity in 1 click",
    shopify_theme_autopilot_confirm: "Theme auto-pilot will push {count} corrections (severity ≥ {sev}) out of {total} findings to the published theme. Continue?",
    shopify_theme_autopilot_nothing: "No theme findings match the chosen severity threshold.",
    apply_log_title: "Apply log (audit trail)",
    apply_log_refresh: "Refresh",
    apply_log_empty: "No corrections pushed yet. Apply a fix from Audit, Rejection or Shopify scan and it will appear here.",
    apply_log_export_pdf: "Download PDF",
    apply_log_hint: "All corrections pushed to Shopify are logged here. Use this as evidence in a Google Merchant re-review request.",
    shopify_autopilot: "Auto-pilot",
    shopify_autopilot_hint: "Auto-select & apply ALL non-compliant fixes in one click",
    shopify_autopilot_confirm: "Auto-pilot will push {fields} corrections across {resources} resources (score ≤ {threshold}) to Shopify. {skipped} resources above threshold will be skipped. Continue?",
    shopify_autopilot_done: "Auto-pilot complete",
    shopify_autopilot_nothing: "Nothing to fix — your scanned resources are all compliant.",
    rejection_history: "Rejection diagnoses",
    rejection_history_empty: "No diagnoses yet. Run your first analysis.",
    rejection_history_load: "Load",
    rejection_history_delete: "Delete",
    rejection_history_confirm_delete: "Delete this diagnostic report?",
    rejection_history_rewrites: "rewrites",
    apply_all_modal_title: "Apply all to Shopify",
    apply_all_modal_submit: "Apply all",
    apply_all_rewrites_header: "Rewrites to push",
    apply_all_no_selection: "No rewrite selected.",
    apply_all_skipped: "Rewrites skipped (phrase not found)",
    apply_all_btn: "Apply all to Shopify",
    rejection_export_pdf: "Download PDF",
    shopify_theme_scan: "Scan theme",
    shopify_theme_scanning: "Scanning theme…",
    shopify_theme_scan_hint: "Read-only audit of your published theme settings (hero, banners, footer…)",
    shopify_theme_scan_results: "Theme audit",
    shopify_theme_strings_scanned: "Strings scanned",
    shopify_theme_findings: "Findings",
    shopify_theme_no_findings: "No theme violations detected — your published theme is clean.",
    shopify_theme_readonly_title: "Read-only audit",
    shopify_theme_readonly_hint: "Fixes are not auto-applied to the theme. Open Shopify › Online Store › Customize and edit each section above.",
  },
  fr: {
    rejection_tab: "Refus",
    rejection_title: "Résolveur de refus Google Merchant",
    rejection_email_label: "Coller l'email de refus Google",
    rejection_email_ph: "Collez ici l'intégralité de l'email reçu de Google Merchant Center...",
    rejection_email_required: "Merci de coller l'email de refus Google",
    rejection_product_url: "URL du produit refusé",
    rejection_product_text: "Texte du produit refusé",
    rejection_product_text_ph: "Collez ici le titre + description du produit refusé...",
    rejection_product_type: "Type de produit (optionnel)",
    rejection_product_type_ph: "ex : bijoux, cosmétique, compléments...",
    rejection_run: "Diagnostiquer & corriger",
    rejection_running: "Analyse en cours...",
    rejection_rewrites: "Problèmes détectés & reformulations",
    rejection_no_issues: "Aucune phrase clairement déclenchante détectée. Vérifiez l'ensemble du catalogue ou contactez le support Google.",
    rejection_appeal_title: "Email d'appel à Google",
    rejection_checklist: "Les 8 sous-catégories Google de « Déclarations trompeuses »",
    rejection_checklist_intro: "Vérifiez chaque point sur l'ensemble de votre catalogue :",
    rejection_policy_link: "Lire la politique officielle Google",
    rejection_cat_1: "Promotions ou offres indisponibles (faux cadeaux, stock manquant)",
    rejection_cat_2: "Prix ou frais incorrects (prix différent du panier, frais cachés)",
    rejection_cat_3: "Recommandations ou affiliations trompeuses (faux avis, faux prix, mentions « recommandé par des experts » sans source)",
    rejection_cat_4: "Affirmations fausses ou trompeuses sur le produit (cures miracles, superlatifs absolus, « 100 % authentique » non prouvé)",
    rejection_cat_5: "Promotions ou remises trompeuses (faux prix barrés, faux -80 %, codes promo invalides)",
    rejection_cat_6: "Disponibilité ou rareté trompeuse (« plus que 2 unités », fausses urgences, faux comptes à rebours)",
    rejection_cat_7: "Conditions de paiement, livraison ou retour cachées ou ambiguës",
    rejection_cat_8: "Identité de l'entreprise cachée ou trompeuse (nom, adresse, contact manquants)",
    cancel: "Annuler",
    apply_modal_title: "Appliquer la correction à Shopify",
    apply_modal_snippet: "Reformulation à pousser",
    apply_modal_connection: "Boutique Shopify",
    apply_modal_resource_type: "Type de ressource",
    apply_modal_search: "Trouver la ressource",
    apply_modal_search_ph: "Rechercher par titre…",
    apply_modal_no_results: "Aucune ressource trouvée. Modifiez votre recherche.",
    apply_modal_field: "Champ ciblé",
    apply_modal_field_hint: "Nous chercherons la phrase originale dans ce champ et la remplacerons par la version corrigée. Le reste du contenu est conservé.",
    apply_modal_applying: "Envoi…",
    apply_modal_submit: "Appliquer à Shopify",
    apply_no_conn: "Aucune boutique Shopify connectée. Connectez-la d'abord depuis l'onglet Shopify.",
    apply_phrase_not_found: "La phrase originale est introuvable dans le champ Shopify. Éditez-la manuellement ou choisissez une autre ressource/un autre champ.",
    diff_inline: "Diff inline",
    diff_inline_hint: "Surlignage mot par mot des suppressions/ajouts dans un seul bloc",
    diff_inline_title: "Reformulation conforme — diff inline",
    diff_side: "Côte à côte",
    diff_side_hint: "Afficher l'original et la suggestion dans deux colonnes séparées",
    diff_removed: "supprimé",
    diff_added: "ajouté",
    product_picker_button: "Choisir des produits",
    product_picker_hint: "Choisissez un sous-ensemble précis de produits à scanner (au lieu des N premiers).",
    product_picker_title: "Choisir les produits à scanner",
    product_picker_loaded: "chargés",
    product_picker_selected: "sélectionnés",
    product_picker_filter_placeholder: "Filtrer par titre…",
    product_picker_select_visible: "Sélectionner visibles",
    product_picker_unselect_visible: "Désélectionner visibles",
    product_picker_clear: "Tout désélectionner",
    product_picker_loading: "Chargement des produits…",
    product_picker_no_match: "Aucun produit ne correspond au filtre sur cette page.",
    product_picker_empty: "Aucun produit sur cette page.",
    product_picker_prev: "Précédent",
    product_picker_next: "Page suivante",
    product_picker_page: "Page",
    product_picker_confirm: "Utiliser la sélection",
    product_picker_picked_count: "Sélection",
    product_picker_reset_hint: "Effacer la sélection (re-scanner tout)",
    product_picker_reset_toast: "Sélection effacée — le scan utilisera la limite par défaut.",
    product_picker_picked_toast: "Produits sélectionnés",
    product_picker_filter_hint: "Filtrer les produits par score d'audit passé",
    product_picker_filter_all: "Tous les produits",
    product_picker_filter_never: "Jamais scannés",
    product_picker_filter_lt90: "Score < 90",
    product_picker_filter_lt70: "Score < 70",
    product_picker_never_scanned: "Jamais scanné",
    product_picker_last_scanned: "Dernier scan",
    auto_route_title: "Auto-routage — appliquer là où la phrase existe vraiment",
    auto_route_hint: "Cherche dans tous les produits, pages, collections, politiques et paramètres du thème ; applique chaque reformulation là où la phrase originale est trouvée. Recommandé.",
    auto_route_or_manual: "Ou choisissez une ressource spécifique manuellement ci-dessous",
    auto_route_button: "Auto-routage & Appliquer",
    auto_route_running: "Recherche dans la boutique…",
    auto_route_applied: "Appliquées",
    auto_route_not_found: "Introuvables",
    auto_route_errors: "Erreurs",
    auto_route_not_found_one: "Introuvable dans la boutique.",
    auto_route_none_found: "Aucune phrase originale n'a été trouvée dans la boutique.",
    auto_route_partial_or_error: "Auto-routage terminé avec des éléments ignorés ou en erreur — voir le détail.",
    auto_route_dry_run_button: "Aperçu seulement",
    auto_route_dry_run_hint: "Cherche dans la boutique sans rien écrire sur Shopify. Vous permet de vérifier ce qui sera modifié avant d'appliquer.",
    auto_route_dry_run_done: "Aperçu terminé — correspondances trouvées",
    auto_route_dry_run_badge: "Aperçu — aucune modification appliquée",
    auto_route_dry_run_matches: "Serait appliqué à",
    apply_success: "Correction appliquée à Shopify",
    apply_updated_fields: "Champs mis à jour",
    apply_warn_title: "Rien appliqué",
    apply_done_title: "Correction appliquée",
    apply_to_shopify_btn: "Appliquer à Shopify",
    apply_replacements_count: "Phrases remplacées",
    shopify_help_auto_title: "Comment se connecter automatiquement (OAuth)",
    shopify_help_auto_step1: "Tapez l'URL complète de votre boutique Shopify — exemple : maboutique.myshopify.com (sans https://, sans chemin)",
    shopify_help_auto_step2: "Cliquez « Install with Shopify » — un nouvel onglet s'ouvre sur l'écran d'autorisation Shopify",
    shopify_help_auto_step3: "Connectez-vous à Shopify (si vous ne l'êtes pas déjà) et confirmez en cliquant « Install app »",
    shopify_help_auto_step4: "Shopify vous renvoie sur MerchantAudit. La connexion apparaît en haut de la liste — terminé.",
    shopify_help_auto_troubleshoot: "Si Shopify retourne « redirect_uri is not whitelisted » ou que vous restez bloqué sur Shopify après Install : l'app OAuth n'est pas configurée pour CE domaine. Utilisez « Manual Custom App » ci-dessous, ou mettez à jour APP_PUBLIC_URL dans votre prod ET l'URL de redirection dans votre Shopify Partners app.",
    shopify_help_manual_title: "Comment se connecter manuellement (Custom App)",
    shopify_help_manual_step1: "Ouvrez votre admin Shopify → Paramètres (en bas à gauche) → Applications et canaux de vente",
    shopify_help_manual_step2: "Cliquez « Développer des applications ». Si le bouton n'apparaît pas, cliquez d'abord « Autoriser le développement d'applications personnalisées », puis rafraîchissez.",
    shopify_help_manual_step3: "Cliquez « Créer une application » → choisissez un nom (par ex. « MerchantAudit ») → « Créer »",
    shopify_help_manual_step4: "Onglet « Configuration » → « Champs d'application de l'API Admin » → « Configurer » → activez : read_products, write_products, read_content, write_content, read_themes, write_themes, read_online_store_navigation, write_online_store_navigation. Enregistrez.",
    shopify_help_manual_step5: "Onglet « Identifiants d'API » → cliquez « Installer l'application » → confirmez « Installer »",
    shopify_help_manual_step6: "Copiez le « Jeton d'accès à l'API Admin » (commence par shpat_…) — il est affiché UNE SEULE FOIS, copiez-le maintenant.",
    shopify_help_manual_step7: "Remplissez les trois champs ci-dessous : un surnom pour la connexion, l'URL de votre boutique (xxx.myshopify.com), le jeton. Cliquez « Connect ».",
    shopify_help_manual_troubleshoot: "Si vous obtenez « Invalid API key or access token » : votre jeton est faux ou vous avez oublié l'étape « Installer l'application ». Si vous obtenez « store_url must end with .myshopify.com » : tapez l'URL sans https:// et sans / final.",
    shopify_token_warning_title: "N'utilisez pas Hoppscotch / Postman pour OAuth",
    shopify_token_warning_body: "Le jeton commençant par shpat_… s'affiche DIRECTEMENT dans votre admin Shopify, onglet « Identifiants d'API » APRÈS avoir cliqué « Installer l'application ». Vous n'avez PAS besoin de faire un échange OAuth manuel avec client_id + client_secret. Si votre jeton commence par shpss_… c'est le client secret, PAS le jeton d'accès.",
    shopify_health_ok: "Tout est vert — OAuth + manuel + audit thème prêts",
    shopify_health_warn: "Globalement OK — problème de configuration mineur",
    shopify_health_manual_only: "OAuth non configuré — connexion Custom App manuelle uniquement",
    shopify_health_broken: "Configuration incomplète — la connexion Shopify va échouer",
    shopify_health_oauth_configured: "Clés OAuth configurées",
    shopify_health_host_match: "App URL correspond à ce domaine",
    shopify_health_encryption: "Chiffrement des jetons prêt",
    shopify_health_scope_read_themes: "Scope read_themes accordé",
    shopify_health_redirect_uri: "URL de callback à whitelister",
    shopify_health_warn_hint: "Ajoutez 'read_themes' à la variable SHOPIFY_APP_SCOPES et redéployez pour activer l'audit du thème.",
    shopify_health_manual_only_hint: "Définissez SHOPIFY_API_KEY + SHOPIFY_API_SECRET dans vos variables d'env pour activer l'installation OAuth en 1 clic. En attendant, utilisez le flow Manual Custom App ci-dessous.",
    shopify_health_broken_hint: "Corrigez les éléments rouges ci-dessus et redéployez. Cause la plus probable : APP_PUBLIC_URL ne correspond pas au domaine que vous utilisez actuellement.",
    shopify_theme_apply_one: "Appliquer",
    shopify_theme_apply_all: "Tout appliquer",
    shopify_theme_applied: "Appliqué",
    shopify_theme_apply_done: "Corrections poussées sur le thème",
    shopify_theme_no_apply: "Aucune correction appliquée (les chemins ne correspondent pas au thème publié).",
    shopify_theme_apply_warn_title: "Écriture sur thème activée.",
    shopify_theme_apply_warn_hint: "Chaque Apply modifie directement config/settings_data.json du thème publié. Vérifiez votre boutique en ligne après application.",
    shopify_theme_autopilot: "Auto-pilote thème",
    shopify_theme_autopilot_hint: "Appliquer TOUS les findings du thème selon la sévérité minimum choisie en 1 clic",
    shopify_theme_autopilot_confirm: "L'Auto-pilote thème va pousser {count} corrections (sévérité ≥ {sev}) parmi {total} findings vers le thème publié. Continuer ?",
    shopify_theme_autopilot_nothing: "Aucun finding ne correspond au seuil de sévérité choisi.",
    apply_log_title: "Journal des corrections (audit trail)",
    apply_log_refresh: "Actualiser",
    apply_log_empty: "Aucune correction poussée pour le moment. Appliquez une correction depuis Audit, Refus ou Scan Shopify et elle apparaîtra ici.",
    apply_log_export_pdf: "Télécharger PDF",
    apply_log_hint: "Toutes les corrections poussées vers Shopify sont enregistrées ici. À fournir comme preuve dans une demande de réexamen Google Merchant.",
    shopify_autopilot: "Auto-pilote",
    shopify_autopilot_hint: "Sélectionner et appliquer TOUTES les corrections non-conformes en 1 clic",
    shopify_autopilot_confirm: "L'Auto-pilote va pousser {fields} corrections sur {resources} ressources (score ≤ {threshold}) vers Shopify. {skipped} ressources au-dessus du seuil seront ignorées. Continuer ?",
    shopify_autopilot_done: "Auto-pilote terminé",
    shopify_autopilot_nothing: "Rien à corriger — toutes vos ressources scannées sont conformes.",
  },
  es: {
    rejection_history: "Diagnostics de refus",
    rejection_history_empty: "Aucun diagnostic pour le moment. Lancez votre première analyse.",
    rejection_history_load: "Charger",
    rejection_history_delete: "Supprimer",
    rejection_history_confirm_delete: "Supprimer ce rapport de diagnostic ?",
    rejection_history_rewrites: "reformulations",
    apply_all_modal_title: "Tout appliquer à Shopify",
    apply_all_modal_submit: "Tout appliquer",
    apply_all_rewrites_header: "Reformulations à pousser",
    apply_all_no_selection: "Aucune reformulation sélectionnée.",
    apply_all_skipped: "Reformulations ignorées (phrase introuvable)",
    apply_all_btn: "Tout appliquer à Shopify",
    rejection_export_pdf: "Télécharger PDF",
    shopify_theme_scan: "Auditer le thème",
    shopify_theme_scanning: "Audit du thème…",
    shopify_theme_scan_hint: "Audit lecture seule des réglages du thème publié (hero, bannières, pied de page…)",
    shopify_theme_scan_results: "Audit du thème",
    shopify_theme_strings_scanned: "Chaînes analysées",
    shopify_theme_findings: "Anomalies",
    shopify_theme_no_findings: "Aucune violation détectée dans le thème — votre thème publié est conforme.",
    shopify_theme_readonly_title: "Audit en lecture seule",
    shopify_theme_readonly_hint: "Les corrections ne sont pas appliquées automatiquement au thème. Ouvrez Shopify › Boutique en ligne › Personnaliser et éditez chaque section ci-dessus.",
  },
  es: {
    rejection_tab: "Rechazo",
    rejection_title: "Resolución de rechazos Google Merchant",
    rejection_email_label: "Pegar el email de rechazo de Google",
    rejection_email_ph: "Pegue aquí el correo completo recibido de Google Merchant Center...",
    rejection_email_required: "Por favor pegue el email de Google",
    rejection_product_url: "URL del producto rechazado",
    rejection_product_text: "Texto del producto rechazado",
    rejection_product_text_ph: "Pegue título + descripción del producto rechazado...",
    rejection_product_type: "Tipo de producto (opcional)",
    rejection_product_type_ph: "ej. joyas, cosmética, suplementos...",
    rejection_run: "Diagnosticar y corregir",
    rejection_running: "Analizando...",
    rejection_rewrites: "Problemas y reescrituras",
    rejection_no_issues: "Sin frases claras. Revise el catálogo completo o contacte con soporte Google.",
    rejection_appeal_title: "Email de apelación a Google",
    rejection_checklist: "Las 8 subcategorías de Google «Tergiversación»",
    rejection_checklist_intro: "Revise cada punto en todo su catálogo:",
    rejection_policy_link: "Leer la política oficial de Google",
    rejection_cat_1: "Promociones u ofertas no disponibles (falsos regalos, stock inexistente)",
    rejection_cat_2: "Precios o tarifas incorrectos (precio distinto al del checkout, gastos ocultos)",
    rejection_cat_3: "Recomendaciones o afiliaciones engañosas (reseñas o premios falsos, expertos sin fuente)",
    rejection_cat_4: "Afirmaciones falsas sobre el producto (curas milagro, superlativos absolutos, «100 % auténtico» sin prueba)",
    rejection_cat_5: "Descuentos o promociones engañosos (precios tachados falsos, -80 % falso, códigos no válidos)",
    rejection_cat_6: "Disponibilidad o escasez engañosa («solo quedan 2», falsas urgencias, falsas cuentas atrás)",
    rejection_cat_7: "Condiciones de pago, envío o devolución ocultas o poco claras",
    rejection_cat_8: "Identidad comercial oculta o tergiversada (nombre, dirección o contacto ausentes)",
    cancel: "Cancelar",
    apply_modal_title: "Aplicar corrección a Shopify",
    apply_modal_snippet: "Reescritura a enviar",
    apply_modal_connection: "Tienda Shopify",
    apply_modal_resource_type: "Tipo de recurso",
    apply_modal_search: "Buscar recurso",
    apply_modal_search_ph: "Buscar por título…",
    apply_modal_no_results: "Sin resultados. Ajuste su búsqueda.",
    apply_modal_field: "Campo objetivo",
    apply_modal_field_hint: "Buscaremos la frase original en este campo y la reemplazaremos por la sugerida. El resto del contenido se conserva.",
    apply_modal_applying: "Enviando…",
    apply_modal_submit: "Aplicar a Shopify",
    apply_no_conn: "Ninguna tienda Shopify conectada. Conéctela desde la pestaña Shopify primero.",
    apply_phrase_not_found: "La frase original no se encontró en el campo Shopify. Edítela manualmente o elija otro recurso/campo.",
    diff_inline: "Diff en línea",
    diff_inline_hint: "Resaltado palabra por palabra de eliminaciones/adiciones en un solo bloque",
    diff_inline_title: "Reformulación conforme — diff en línea",
    diff_side: "Lado a lado",
    diff_side_hint: "Mostrar el original y la sugerencia en dos columnas separadas",
    diff_removed: "eliminado",
    diff_added: "añadido",
    product_picker_button: "Elegir productos",
    product_picker_hint: "Elija un subconjunto específico de productos para escanear (en lugar de los N primeros).",
    product_picker_title: "Elegir productos a escanear",
    product_picker_loaded: "cargados",
    product_picker_selected: "seleccionados",
    product_picker_filter_placeholder: "Filtrar por título…",
    product_picker_select_visible: "Seleccionar visibles",
    product_picker_unselect_visible: "Deseleccionar visibles",
    product_picker_clear: "Limpiar todo",
    product_picker_loading: "Cargando productos…",
    product_picker_no_match: "Ningún producto coincide con el filtro en esta página.",
    product_picker_empty: "Sin productos en esta página.",
    product_picker_prev: "Anterior",
    product_picker_next: "Página siguiente",
    product_picker_page: "Página",
    product_picker_confirm: "Usar selección",
    product_picker_picked_count: "Selección",
    product_picker_reset_hint: "Limpiar selección (escanear todo de nuevo)",
    product_picker_reset_toast: "Selección limpiada — el escaneo usará el límite por defecto.",
    product_picker_picked_toast: "Productos seleccionados",
    product_picker_filter_hint: "Filtrar productos por puntuación de auditoría anterior",
    product_picker_filter_all: "Todos los productos",
    product_picker_filter_never: "Nunca escaneados",
    product_picker_filter_lt90: "Puntuación < 90",
    product_picker_filter_lt70: "Puntuación < 70",
    product_picker_never_scanned: "Nunca escaneado",
    product_picker_last_scanned: "Último escaneo",
    auto_route_title: "Auto-enrutado — aplicar donde la frase exista realmente",
    auto_route_hint: "Busca en todos los productos, páginas, colecciones, políticas y configuración del tema; aplica cada reescritura donde se encuentra la frase original. Recomendado.",
    auto_route_or_manual: "O elige un recurso específico manualmente abajo",
    auto_route_button: "Auto-enrutar y aplicar",
    auto_route_running: "Buscando en la tienda…",
    auto_route_applied: "Aplicadas",
    auto_route_not_found: "No encontradas",
    auto_route_errors: "Errores",
    auto_route_not_found_one: "No se encontró en la tienda.",
    auto_route_none_found: "Ninguna de las frases originales se encontró en la tienda.",
    auto_route_partial_or_error: "Auto-enrutado completado con elementos omitidos o con errores — ver detalles.",
    auto_route_dry_run_button: "Solo vista previa",
    auto_route_dry_run_hint: "Busca en la tienda sin escribir nada en Shopify. Le permite verificar qué se modificará antes de aplicar.",
    auto_route_dry_run_done: "Vista previa completada — coincidencias encontradas",
    auto_route_dry_run_badge: "Vista previa — no se aplicó nada",
    auto_route_dry_run_matches: "Se aplicaría a",
    apply_success: "Corrección aplicada a Shopify",
    apply_updated_fields: "Campos actualizados",
    apply_warn_title: "Nada aplicado",
    apply_done_title: "Corrección aplicada",
    apply_to_shopify_btn: "Aplicar a Shopify",
    apply_replacements_count: "Frases reemplazadas",
    shopify_help_auto_title: "Cómo conectarse automáticamente (OAuth)",
    shopify_help_auto_step1: "Escriba la URL completa de su tienda Shopify — ejemplo: mitienda.myshopify.com (sin https://, sin ruta)",
    shopify_help_auto_step2: "Haga clic en «Install with Shopify» — se abre una nueva pestaña en la pantalla de autorización de Shopify",
    shopify_help_auto_step3: "Inicie sesión en Shopify (si aún no lo está) y confirme haciendo clic en «Install app»",
    shopify_help_auto_step4: "Shopify le devolverá a MerchantAudit. La conexión aparece en la parte superior de esta lista — listo.",
    shopify_help_auto_troubleshoot: "Si Shopify devuelve «redirect_uri is not whitelisted» o se queda atascado después de Install: la app OAuth no está configurada para ESTE dominio. Use Manual Custom App debajo, o actualice APP_PUBLIC_URL en su entorno de producción y la URL de redirección en su app de Shopify Partners.",
    shopify_help_manual_title: "Cómo conectarse manualmente (Custom App)",
    shopify_help_manual_step1: "Abra su admin de Shopify → Configuración (abajo a la izquierda) → Aplicaciones y canales de venta",
    shopify_help_manual_step2: "Haga clic en «Desarrollar aplicaciones». Si el botón no aparece, primero haga clic en «Permitir el desarrollo de aplicaciones personalizadas» y actualice.",
    shopify_help_manual_step3: "Haga clic en «Crear una aplicación» → elija un nombre (p. ej. «MerchantAudit») → «Crear»",
    shopify_help_manual_step4: "Pestaña «Configuración» → «Alcances de API Admin» → «Configurar» → active: read_products, write_products, read_content, write_content, read_themes, write_themes, read_online_store_navigation, write_online_store_navigation. Guardar.",
    shopify_help_manual_step5: "Pestaña «Credenciales de API» → haga clic en «Instalar aplicación» → confirme",
    shopify_help_manual_step6: "Copie el «Token de acceso a la API Admin» (comienza con shpat_…) — solo se muestra UNA VEZ, cópielo ahora.",
    shopify_help_manual_step7: "Complete los tres campos: un nombre para la conexión, la URL de su tienda (xxx.myshopify.com), el token. Haga clic en «Connect».",
    shopify_help_manual_troubleshoot: "Si obtiene «Invalid API key or access token»: su token es incorrecto o olvidó el paso «Instalar aplicación». Si obtiene «store_url must end with .myshopify.com»: escriba la URL sin https:// y sin / final.",
    shopify_token_warning_title: "No use Hoppscotch / Postman para OAuth",
    shopify_token_warning_body: "El token que empieza por shpat_… se muestra DIRECTAMENTE en su admin Shopify, en la pestaña «Credenciales de API» DESPUÉS de hacer clic en «Instalar aplicación». NO necesita hacer un intercambio OAuth manual con client_id + client_secret. Si su token empieza por shpss_… ese es el client secret, NO el token de acceso.",
    shopify_health_ok: "Todo en verde — OAuth + manual + auditoría del tema listos",
    shopify_health_warn: "Mayormente OK — problema de configuración menor",
    shopify_health_manual_only: "OAuth no configurado — solo conexión Custom App manual",
    shopify_health_broken: "Configuración incompleta — la conexión Shopify fallará",
    shopify_health_oauth_configured: "Claves OAuth configuradas",
    shopify_health_host_match: "App URL coincide con este dominio",
    shopify_health_encryption: "Cifrado de tokens listo",
    shopify_health_scope_read_themes: "Permiso read_themes concedido",
    shopify_health_redirect_uri: "URL de devolución a autorizar",
    shopify_health_warn_hint: "Añada 'read_themes' a SHOPIFY_APP_SCOPES y vuelva a desplegar para activar la auditoría del tema.",
    shopify_health_manual_only_hint: "Defina SHOPIFY_API_KEY + SHOPIFY_API_SECRET en su entorno para activar la instalación OAuth de 1 clic. Mientras tanto, use Custom App manual.",
    shopify_health_broken_hint: "Corrija los elementos rojos y vuelva a desplegar. Causa más probable: APP_PUBLIC_URL no coincide con el dominio actual.",
    shopify_theme_apply_one: "Aplicar",
    shopify_theme_apply_all: "Aplicar todo",
    shopify_theme_applied: "Aplicado",
    shopify_theme_apply_done: "Correcciones enviadas al tema",
    shopify_theme_no_apply: "No se aplicó ninguna corrección (los caminos no coinciden con el tema activo).",
    shopify_theme_apply_warn_title: "Escritura sobre tema activada.",
    shopify_theme_apply_warn_hint: "Cada Aplicar modifica directamente config/settings_data.json del tema activo. Previsualice su tienda después.",
    shopify_theme_autopilot: "Auto-piloto del tema",
    shopify_theme_autopilot_hint: "Aplicar TODOS los findings del tema según la severidad mínima elegida en 1 clic",
    shopify_theme_autopilot_confirm: "El Auto-piloto del tema enviará {count} correcciones (severidad ≥ {sev}) de {total} findings al tema publicado. ¿Continuar?",
    shopify_theme_autopilot_nothing: "Ningún finding del tema coincide con el umbral de severidad elegido.",
    apply_log_title: "Registro de correcciones (audit trail)",
    apply_log_refresh: "Actualizar",
    apply_log_empty: "Aún no se han enviado correcciones. Aplique una desde Audit, Rechazo o Scan Shopify y aparecerá aquí.",
    apply_log_export_pdf: "Descargar PDF",
    apply_log_hint: "Todas las correcciones enviadas a Shopify se registran aquí. Use esto como prueba en una solicitud de reevaluación en Google Merchant.",
    shopify_autopilot: "Auto-piloto",
    shopify_autopilot_hint: "Seleccionar y aplicar TODAS las correcciones no conformes en 1 clic",
    shopify_autopilot_confirm: "El Auto-piloto enviará {fields} correcciones en {resources} recursos (puntuación ≤ {threshold}) a Shopify. Se omitirán {skipped} recursos por encima del umbral. ¿Continuar?",
    shopify_autopilot_done: "Auto-piloto completado",
    shopify_autopilot_nothing: "Nada que corregir — sus recursos escaneados son conformes.",
    rejection_history: "Diagnósticos de rechazo",
    rejection_history_empty: "Aún no hay diagnósticos. Lanza tu primer análisis.",
    rejection_history_load: "Cargar",
    rejection_history_delete: "Eliminar",
    rejection_history_confirm_delete: "¿Eliminar este informe de diagnóstico?",
    rejection_history_rewrites: "reescrituras",
    apply_all_modal_title: "Aplicar todo a Shopify",
    apply_all_modal_submit: "Aplicar todo",
    apply_all_rewrites_header: "Reescrituras a enviar",
    apply_all_no_selection: "Ninguna reescritura seleccionada.",
    apply_all_skipped: "Reescrituras omitidas (frase no encontrada)",
    apply_all_btn: "Aplicar todo a Shopify",
    rejection_export_pdf: "Descargar PDF",
    shopify_theme_scan: "Auditar el tema",
    shopify_theme_scanning: "Auditando el tema…",
    shopify_theme_scan_hint: "Auditoría de solo lectura de los ajustes del tema publicado (hero, banners, pie…)",
    shopify_theme_scan_results: "Auditoría del tema",
    shopify_theme_strings_scanned: "Cadenas analizadas",
    shopify_theme_findings: "Hallazgos",
    shopify_theme_no_findings: "Sin infracciones en el tema — su tema publicado es conforme.",
    shopify_theme_readonly_title: "Auditoría de solo lectura",
    shopify_theme_readonly_hint: "Las correcciones no se aplican automáticamente al tema. Abra Shopify › Tienda online › Personalizar y edite cada sección.",
  },
  it: {
    rejection_tab: "Rifiuto",
    rejection_title: "Risolutore rifiuti Google Merchant",
    rejection_email_label: "Incolla l'email di rifiuto Google",
    rejection_email_ph: "Incolla qui l'email completa ricevuta da Google Merchant Center...",
    rejection_email_required: "Incolla l'email di Google",
    rejection_product_url: "URL del prodotto rifiutato",
    rejection_product_text: "Testo del prodotto rifiutato",
    rejection_product_text_ph: "Incolla titolo + descrizione del prodotto rifiutato...",
    rejection_product_type: "Tipo prodotto (opzionale)",
    rejection_product_type_ph: "es. gioielli, cosmetici, integratori...",
    rejection_run: "Diagnostica & correggi",
    rejection_running: "Analisi in corso...",
    rejection_rewrites: "Problemi & riformulazioni",
    rejection_no_issues: "Nessuna frase chiara rilevata. Verifica l'intero catalogo o contatta il supporto Google.",
    rejection_appeal_title: "Email di ricorso a Google",
    rejection_checklist: "Le 8 sottocategorie Google di «Travisamento»",
    rejection_checklist_intro: "Verifica ogni punto sull'intero catalogo:",
    rejection_policy_link: "Leggi la policy ufficiale di Google",
    rejection_cat_1: "Promozioni o offerte non disponibili (falsi omaggi, scorte assenti)",
    rejection_cat_2: "Prezzi o costi inesatti (prezzo diverso al checkout, spese nascoste)",
    rejection_cat_3: "Sponsorizzazioni o affiliazioni ingannevoli (recensioni o premi falsi, esperti senza fonte)",
    rejection_cat_4: "Affermazioni false o ingannevoli sul prodotto (cure miracolose, superlativi assoluti, «100 % autentico» non provato)",
    rejection_cat_5: "Sconti o promozioni ingannevoli (prezzi barrati falsi, -80 % falso, codici non validi)",
    rejection_cat_6: "Disponibilità o scarsità ingannevole («solo 2 rimasti», false urgenze, falsi countdown)",
    rejection_cat_7: "Termini di pagamento, spedizione o reso nascosti o poco chiari",
    rejection_cat_8: "Identità aziendale nascosta o travisata (nome, indirizzo o contatto assenti)",
    cancel: "Annulla",
    apply_modal_title: "Applica la correzione a Shopify",
    apply_modal_snippet: "Riformulazione da inviare",
    apply_modal_connection: "Negozio Shopify",
    apply_modal_resource_type: "Tipo di risorsa",
    apply_modal_search: "Trova risorsa",
    apply_modal_search_ph: "Cerca per titolo…",
    apply_modal_no_results: "Nessun risultato. Modifica la ricerca.",
    apply_modal_field: "Campo bersaglio",
    apply_modal_field_hint: "Cercheremo la frase originale in questo campo e la sostituiremo con quella suggerita. Il resto del contenuto è preservato.",
    apply_modal_applying: "Invio…",
    apply_modal_submit: "Applica a Shopify",
    apply_no_conn: "Nessun negozio Shopify connesso. Collegalo prima dalla scheda Shopify.",
    apply_phrase_not_found: "La frase originale non è stata trovata nel campo Shopify. Modificala manualmente o scegli un'altra risorsa/campo.",
    diff_inline: "Diff in linea",
    diff_inline_hint: "Evidenziazione parola per parola di rimozioni/aggiunte in un unico blocco",
    diff_inline_title: "Riformulazione conforme — diff in linea",
    diff_side: "Affiancato",
    diff_side_hint: "Mostra originale e suggerimento in due colonne separate",
    diff_removed: "rimosso",
    diff_added: "aggiunto",
    product_picker_button: "Scegli prodotti",
    product_picker_hint: "Scegli un sottoinsieme specifico di prodotti da scansionare (invece dei primi N).",
    product_picker_title: "Scegli i prodotti da scansionare",
    product_picker_loaded: "caricati",
    product_picker_selected: "selezionati",
    product_picker_filter_placeholder: "Filtra per titolo…",
    product_picker_select_visible: "Seleziona visibili",
    product_picker_unselect_visible: "Deseleziona visibili",
    product_picker_clear: "Cancella tutto",
    product_picker_loading: "Caricamento prodotti…",
    product_picker_no_match: "Nessun prodotto corrisponde al filtro in questa pagina.",
    product_picker_empty: "Nessun prodotto in questa pagina.",
    product_picker_prev: "Indietro",
    product_picker_next: "Pagina successiva",
    product_picker_page: "Pagina",
    product_picker_confirm: "Usa selezione",
    product_picker_picked_count: "Selezione",
    product_picker_reset_hint: "Cancella selezione (riscansiona tutto)",
    product_picker_reset_toast: "Selezione cancellata — la scansione userà il limite predefinito.",
    product_picker_picked_toast: "Prodotti selezionati",
    product_picker_filter_hint: "Filtra i prodotti per punteggio di audit precedente",
    product_picker_filter_all: "Tutti i prodotti",
    product_picker_filter_never: "Mai scansionati",
    product_picker_filter_lt90: "Punteggio < 90",
    product_picker_filter_lt70: "Punteggio < 70",
    product_picker_never_scanned: "Mai scansionato",
    product_picker_last_scanned: "Ultima scansione",
    auto_route_title: "Auto-routing — applica dove la frase esiste davvero",
    auto_route_hint: "Cerca in tutti i prodotti, pagine, collezioni, policy e impostazioni del tema; applica ogni riscrittura dove viene trovata la frase originale. Consigliato.",
    auto_route_or_manual: "Oppure scegli manualmente una risorsa specifica qui sotto",
    auto_route_button: "Auto-routing e applica",
    auto_route_running: "Ricerca nel negozio…",
    auto_route_applied: "Applicate",
    auto_route_not_found: "Non trovate",
    auto_route_errors: "Errori",
    auto_route_not_found_one: "Non trovata nel negozio.",
    auto_route_none_found: "Nessuna delle frasi originali è stata trovata nel negozio.",
    auto_route_partial_or_error: "Auto-routing completato con elementi saltati o con errori — vedi dettagli.",
    auto_route_dry_run_button: "Solo anteprima",
    auto_route_dry_run_hint: "Cerca nel negozio senza scrivere nulla su Shopify. Permette di verificare cosa verrà modificato prima di applicare.",
    auto_route_dry_run_done: "Anteprima completata — corrispondenze trovate",
    auto_route_dry_run_badge: "Anteprima — nulla è stato modificato",
    auto_route_dry_run_matches: "Sarebbe applicato a",
    apply_success: "Correzione applicata a Shopify",
    apply_updated_fields: "Campi aggiornati",
    apply_warn_title: "Nulla applicato",
    apply_done_title: "Correzione applicata",
    apply_to_shopify_btn: "Applica a Shopify",
    apply_replacements_count: "Frasi sostituite",
    shopify_help_auto_title: "Come connettersi automaticamente (OAuth)",
    shopify_help_auto_step1: "Inserisci l'URL completo del tuo negozio Shopify — esempio: mionegozio.myshopify.com (senza https://, senza percorso)",
    shopify_help_auto_step2: "Clicca «Install with Shopify» — si apre una nuova scheda con la schermata di autorizzazione Shopify",
    shopify_help_auto_step3: "Accedi a Shopify (se non lo sei già) e conferma cliccando «Install app»",
    shopify_help_auto_step4: "Shopify ti reindirizzerà a MerchantAudit. La connessione appare in cima a questo elenco — fatto.",
    shopify_help_auto_troubleshoot: "Se Shopify restituisce «redirect_uri is not whitelisted» o resti bloccato dopo Install: l'app OAuth non è configurata per QUESTO dominio. Usa Manual Custom App qui sotto, o aggiorna APP_PUBLIC_URL nell'ambiente di produzione e l'URL di redirect nella tua app Shopify Partners.",
    shopify_help_manual_title: "Come connettersi manualmente (Custom App)",
    shopify_help_manual_step1: "Apri l'admin Shopify → Impostazioni (in basso a sinistra) → App e canali di vendita",
    shopify_help_manual_step2: "Clicca «Sviluppa app». Se il pulsante non è visibile, clicca prima «Consenti lo sviluppo di app personalizzate», poi aggiorna.",
    shopify_help_manual_step3: "Clicca «Crea un'app» → scegli un nome (es. «MerchantAudit») → «Crea»",
    shopify_help_manual_step4: "Scheda «Configurazione» → «Ambiti di accesso API Admin» → «Configura» → abilita: read_products, write_products, read_content, write_content, read_themes, write_themes, read_online_store_navigation, write_online_store_navigation. Salva.",
    shopify_help_manual_step5: "Scheda «Credenziali API» → clicca «Installa app» → conferma",
    shopify_help_manual_step6: "Copia il «Token di accesso API Admin» (inizia con shpat_…) — viene mostrato SOLO UNA VOLTA, copialo subito.",
    shopify_help_manual_step7: "Compila i tre campi: un nome per la connessione, l'URL del negozio (xxx.myshopify.com), il token. Clicca «Connect».",
    shopify_help_manual_troubleshoot: "Se ottieni «Invalid API key or access token»: il token è sbagliato o hai saltato «Installa app». Se ottieni «store_url must end with .myshopify.com»: scrivi l'URL senza https:// e senza / finale.",
    shopify_token_warning_title: "Non usare Hoppscotch / Postman per OAuth",
    shopify_token_warning_body: "Il token che inizia con shpat_… viene mostrato DIRETTAMENTE nell'admin Shopify, scheda «Credenziali API» DOPO aver cliccato su «Installa app». NON serve fare uno scambio OAuth manuale con client_id + client_secret. Se il tuo token inizia con shpss_… quello è il client secret, NON il token di accesso.",
    shopify_health_ok: "Tutto verde — OAuth + manuale + audit del tema pronti",
    shopify_health_warn: "Per lo più OK — problema di configurazione minore",
    shopify_health_manual_only: "OAuth non configurato — solo connessione Custom App manuale",
    shopify_health_broken: "Configurazione incompleta — la connessione Shopify fallirà",
    shopify_health_oauth_configured: "Chiavi OAuth configurate",
    shopify_health_host_match: "App URL corrisponde a questo dominio",
    shopify_health_encryption: "Cifratura dei token pronta",
    shopify_health_scope_read_themes: "Scope read_themes concesso",
    shopify_health_redirect_uri: "URL di callback da autorizzare",
    shopify_health_warn_hint: "Aggiungi 'read_themes' a SHOPIFY_APP_SCOPES e ridistribuisci per attivare l'audit del tema.",
    shopify_health_manual_only_hint: "Imposta SHOPIFY_API_KEY + SHOPIFY_API_SECRET nel tuo ambiente per attivare l'installazione OAuth in 1 clic. Nel frattempo usa Custom App manuale.",
    shopify_health_broken_hint: "Correggi gli elementi in rosso sopra e ridistribuisci. Causa più probabile: APP_PUBLIC_URL non corrisponde al dominio attuale.",
    shopify_theme_apply_one: "Applica",
    shopify_theme_apply_all: "Applica tutto",
    shopify_theme_applied: "Applicato",
    shopify_theme_apply_done: "Correzioni inviate al tema",
    shopify_theme_no_apply: "Nessuna correzione applicata (i percorsi non corrispondono al tema attivo).",
    shopify_theme_apply_warn_title: "Scrittura sul tema attivata.",
    shopify_theme_apply_warn_hint: "Ogni Applica modifica direttamente config/settings_data.json del tema attivo. Visualizza l'anteprima del tuo negozio dopo l'applicazione.",
    shopify_theme_autopilot: "Auto-pilota tema",
    shopify_theme_autopilot_hint: "Applica TUTTI i findings del tema secondo la severità minima scelta in 1 clic",
    shopify_theme_autopilot_confirm: "L'Auto-pilota tema invierà {count} correzioni (severità ≥ {sev}) su {total} findings al tema pubblicato. Continuare?",
    shopify_theme_autopilot_nothing: "Nessun finding del tema corrisponde alla soglia di severità scelta.",
    apply_log_title: "Registro delle correzioni (audit trail)",
    apply_log_refresh: "Aggiorna",
    apply_log_empty: "Nessuna correzione inviata ancora. Applica una correzione da Audit, Rifiuto o Scan Shopify e apparirà qui.",
    apply_log_export_pdf: "Scarica PDF",
    apply_log_hint: "Tutte le correzioni inviate a Shopify vengono registrate qui. Usalo come prova in una richiesta di riesame Google Merchant.",
    shopify_autopilot: "Auto-pilota",
    shopify_autopilot_hint: "Seleziona e applica TUTTE le correzioni non conformi in 1 clic",
    shopify_autopilot_confirm: "L'Auto-pilota invierà {fields} correzioni su {resources} risorse (punteggio ≤ {threshold}) a Shopify. {skipped} risorse sopra la soglia saranno ignorate. Continuare?",
    shopify_autopilot_done: "Auto-pilota completato",
    shopify_autopilot_nothing: "Niente da correggere — le tue risorse scansionate sono conformi.",
    rejection_history: "Diagnosi di rifiuto",
    rejection_history_empty: "Nessuna diagnosi ancora. Avvia la prima analisi.",
    rejection_history_load: "Carica",
    rejection_history_delete: "Elimina",
    rejection_history_confirm_delete: "Eliminare questo report di diagnosi?",
    rejection_history_rewrites: "riformulazioni",
    apply_all_modal_title: "Applica tutto a Shopify",
    apply_all_modal_submit: "Applica tutto",
    apply_all_rewrites_header: "Riformulazioni da inviare",
    apply_all_no_selection: "Nessuna riformulazione selezionata.",
    apply_all_skipped: "Riformulazioni saltate (frase non trovata)",
    apply_all_btn: "Applica tutto a Shopify",
    rejection_export_pdf: "Scarica PDF",
    shopify_theme_scan: "Audit del tema",
    shopify_theme_scanning: "Audit del tema…",
    shopify_theme_scan_hint: "Audit di sola lettura delle impostazioni del tema pubblicato (hero, banner, footer…)",
    shopify_theme_scan_results: "Audit del tema",
    shopify_theme_strings_scanned: "Stringhe analizzate",
    shopify_theme_findings: "Rilievi",
    shopify_theme_no_findings: "Nessuna violazione nel tema — il tuo tema pubblicato è conforme.",
    shopify_theme_readonly_title: "Audit in sola lettura",
    shopify_theme_readonly_hint: "Le correzioni non vengono applicate automaticamente al tema. Apri Shopify › Negozio online › Personalizza e modifica ogni sezione.",
  },
  pt: {
    rejection_tab: "Rejeição",
    rejection_title: "Resolutor de rejeições Google Merchant",
    rejection_email_label: "Cole o email de rejeição do Google",
    rejection_email_ph: "Cole aqui o email completo recebido do Google Merchant Center...",
    rejection_email_required: "Cole o email do Google",
    rejection_product_url: "URL do produto rejeitado",
    rejection_product_text: "Texto do produto rejeitado",
    rejection_product_text_ph: "Cole título + descrição do produto rejeitado...",
    rejection_product_type: "Tipo de produto (opcional)",
    rejection_product_type_ph: "ex: joias, cosméticos, suplementos...",
    rejection_run: "Diagnosticar & corrigir",
    rejection_running: "Analisando...",
    rejection_rewrites: "Problemas & reescritas",
    rejection_no_issues: "Nenhuma frase clara detectada. Revise o catálogo ou contacte o suporte Google.",
    rejection_appeal_title: "Email de recurso ao Google",
    rejection_checklist: "As 8 subcategorias Google de «Deturpação»",
    rejection_checklist_intro: "Verifique cada item em todo o seu catálogo:",
    rejection_policy_link: "Ler a política oficial do Google",
    rejection_cat_1: "Promoções ou ofertas indisponíveis (brindes falsos, sem stock)",
    rejection_cat_2: "Preços ou taxas incorretos (preço diferente no checkout, custos ocultos)",
    rejection_cat_3: "Recomendações ou afiliações enganosas (avaliações ou prémios falsos, especialistas sem fonte)",
    rejection_cat_4: "Afirmações falsas sobre o produto (curas milagrosas, superlativos absolutos, «100 % autêntico» não provado)",
    rejection_cat_5: "Descontos ou promoções enganosos (preços riscados falsos, -80 % falso, códigos inválidos)",
    rejection_cat_6: "Disponibilidade ou escassez enganosa («só restam 2», urgências falsas, contagens regressivas falsas)",
    rejection_cat_7: "Condições de pagamento, envio ou devolução ocultas ou pouco claras",
    rejection_cat_8: "Identidade da empresa oculta ou deturpada (nome, morada ou contacto ausentes)",
    cancel: "Cancelar",
    apply_modal_title: "Aplicar correção ao Shopify",
    apply_modal_snippet: "Reescrita a enviar",
    apply_modal_connection: "Loja Shopify",
    apply_modal_resource_type: "Tipo de recurso",
    apply_modal_search: "Procurar recurso",
    apply_modal_search_ph: "Pesquisar por título…",
    apply_modal_no_results: "Sem resultados. Ajuste a pesquisa.",
    apply_modal_field: "Campo alvo",
    apply_modal_field_hint: "Vamos procurar a frase original neste campo e substituí-la pela sugerida. O resto do conteúdo é preservado.",
    apply_modal_applying: "A enviar…",
    apply_modal_submit: "Aplicar ao Shopify",
    apply_no_conn: "Nenhuma loja Shopify ligada. Ligue uma a partir do separador Shopify primeiro.",
    apply_phrase_not_found: "A frase original não foi encontrada no campo Shopify. Edite-a manualmente ou escolha outro recurso/campo.",
    diff_inline: "Diff em linha",
    diff_inline_hint: "Destaque palavra por palavra de remoções/adições num único bloco",
    diff_inline_title: "Reformulação conforme — diff em linha",
    diff_side: "Lado a lado",
    diff_side_hint: "Mostrar original e sugestão em duas colunas separadas",
    diff_removed: "removido",
    diff_added: "adicionado",
    product_picker_button: "Escolher produtos",
    product_picker_hint: "Escolha um subconjunto específico de produtos para verificar (em vez dos N primeiros).",
    product_picker_title: "Escolher produtos para verificar",
    product_picker_loaded: "carregados",
    product_picker_selected: "selecionados",
    product_picker_filter_placeholder: "Filtrar por título…",
    product_picker_select_visible: "Selecionar visíveis",
    product_picker_unselect_visible: "Desselecionar visíveis",
    product_picker_clear: "Limpar tudo",
    product_picker_loading: "A carregar produtos…",
    product_picker_no_match: "Nenhum produto corresponde ao filtro nesta página.",
    product_picker_empty: "Sem produtos nesta página.",
    product_picker_prev: "Anterior",
    product_picker_next: "Página seguinte",
    product_picker_page: "Página",
    product_picker_confirm: "Usar seleção",
    product_picker_picked_count: "Seleção",
    product_picker_reset_hint: "Limpar seleção (verificar tudo novamente)",
    product_picker_reset_toast: "Seleção limpa — a verificação usará o limite padrão.",
    product_picker_picked_toast: "Produtos selecionados",
    product_picker_filter_hint: "Filtrar produtos por pontuação de auditoria anterior",
    product_picker_filter_all: "Todos os produtos",
    product_picker_filter_never: "Nunca verificados",
    product_picker_filter_lt90: "Pontuação < 90",
    product_picker_filter_lt70: "Pontuação < 70",
    product_picker_never_scanned: "Nunca verificado",
    product_picker_last_scanned: "Última verificação",
    auto_route_title: "Roteamento automático — aplicar onde a frase realmente existe",
    auto_route_hint: "Busca em todos os produtos, páginas, coleções, políticas e configurações do tema; aplica cada reescrita onde a frase original for encontrada. Recomendado.",
    auto_route_or_manual: "Ou escolha um recurso específico manualmente abaixo",
    auto_route_button: "Rotear e aplicar",
    auto_route_running: "Buscando na loja…",
    auto_route_applied: "Aplicadas",
    auto_route_not_found: "Não encontradas",
    auto_route_errors: "Erros",
    auto_route_not_found_one: "Não encontrada na loja.",
    auto_route_none_found: "Nenhuma das frases originais foi encontrada na loja.",
    auto_route_partial_or_error: "Roteamento concluído com itens ignorados ou com erros — ver detalhes.",
    auto_route_dry_run_button: "Apenas visualizar",
    auto_route_dry_run_hint: "Busca na loja sem escrever nada no Shopify. Permite verificar o que será alterado antes de aplicar.",
    auto_route_dry_run_done: "Visualização concluída — correspondências encontradas",
    auto_route_dry_run_badge: "Visualização — nada foi alterado",
    auto_route_dry_run_matches: "Seria aplicado a",
    apply_success: "Correção aplicada ao Shopify",
    apply_updated_fields: "Campos atualizados",
    apply_warn_title: "Nada aplicado",
    apply_done_title: "Correção aplicada",
    apply_to_shopify_btn: "Aplicar ao Shopify",
    apply_replacements_count: "Frases substituídas",
    shopify_help_auto_title: "Como ligar automaticamente (OAuth)",
    shopify_help_auto_step1: "Escreva o URL completo da sua loja Shopify — exemplo: minhaloja.myshopify.com (sem https://, sem caminho)",
    shopify_help_auto_step2: "Clique em «Install with Shopify» — abre-se um novo separador com o ecrã de autorização do Shopify",
    shopify_help_auto_step3: "Inicie sessão no Shopify (se ainda não o fez) e confirme clicando em «Install app»",
    shopify_help_auto_step4: "O Shopify redireciona-o para o MerchantAudit. A ligação aparece no topo desta lista — pronto.",
    shopify_help_auto_troubleshoot: "Se o Shopify devolver «redirect_uri is not whitelisted» ou ficar preso após Install: a app OAuth não está configurada para ESTE domínio. Use Manual Custom App abaixo, ou atualize APP_PUBLIC_URL no seu ambiente de produção e o URL de redireção na sua app Shopify Partners.",
    shopify_help_manual_title: "Como ligar manualmente (Custom App)",
    shopify_help_manual_step1: "Abra o admin Shopify → Definições (em baixo à esquerda) → Aplicações e canais de venda",
    shopify_help_manual_step2: "Clique em «Desenvolver aplicações». Se o botão não aparecer, clique primeiro em «Permitir desenvolvimento de aplicações personalizadas», depois atualize.",
    shopify_help_manual_step3: "Clique em «Criar uma aplicação» → escolha um nome (p. ex. «MerchantAudit») → «Criar»",
    shopify_help_manual_step4: "Separador «Configuração» → «Âmbitos de acesso API Admin» → «Configurar» → ative: read_products, write_products, read_content, write_content, read_themes, write_themes, read_online_store_navigation, write_online_store_navigation. Guarde.",
    shopify_help_manual_step5: "Separador «Credenciais API» → clique em «Instalar aplicação» → confirme",
    shopify_help_manual_step6: "Copie o «Token de acesso API Admin» (começa por shpat_…) — só é mostrado UMA VEZ, copie-o agora.",
    shopify_help_manual_step7: "Preencha os três campos: um nome para a ligação, o URL da loja (xxx.myshopify.com), o token. Clique em «Connect».",
    shopify_help_manual_troubleshoot: "Se obtiver «Invalid API key or access token»: o token está errado ou esqueceu-se de «Instalar aplicação». Se obtiver «store_url must end with .myshopify.com»: escreva o URL sem https:// e sem / final.",
    shopify_token_warning_title: "Não use Hoppscotch / Postman para OAuth",
    shopify_token_warning_body: "O token que começa por shpat_… é mostrado DIRETAMENTE no seu admin Shopify, no separador «Credenciais de API» DEPOIS de clicar em «Instalar aplicação». NÃO precisa de fazer uma troca OAuth manual com client_id + client_secret. Se o seu token começa por shpss_… esse é o client secret, NÃO o token de acesso.",
    shopify_health_ok: "Tudo verde — OAuth + manual + auditoria do tema prontos",
    shopify_health_warn: "Maioritariamente OK — problema menor de configuração",
    shopify_health_manual_only: "OAuth não configurado — só ligação Custom App manual",
    shopify_health_broken: "Configuração incompleta — a ligação Shopify vai falhar",
    shopify_health_oauth_configured: "Chaves OAuth configuradas",
    shopify_health_host_match: "App URL corresponde a este domínio",
    shopify_health_encryption: "Cifragem de tokens pronta",
    shopify_health_scope_read_themes: "Permissão read_themes concedida",
    shopify_health_redirect_uri: "URL de retorno a autorizar",
    shopify_health_warn_hint: "Adicione 'read_themes' a SHOPIFY_APP_SCOPES e reimplante para ativar a auditoria do tema.",
    shopify_health_manual_only_hint: "Defina SHOPIFY_API_KEY + SHOPIFY_API_SECRET no seu ambiente para ativar a instalação OAuth 1-clique. Entretanto use Custom App manual.",
    shopify_health_broken_hint: "Corrija os itens vermelhos acima e reimplante. Causa provável: APP_PUBLIC_URL não corresponde ao domínio atual.",
    shopify_theme_apply_one: "Aplicar",
    shopify_theme_apply_all: "Aplicar tudo",
    shopify_theme_applied: "Aplicado",
    shopify_theme_apply_done: "Correções enviadas ao tema",
    shopify_theme_no_apply: "Nenhuma correção aplicada (os caminhos não correspondem ao tema ativo).",
    shopify_theme_apply_warn_title: "Escrita no tema ativada.",
    shopify_theme_apply_warn_hint: "Cada Aplicar modifica diretamente config/settings_data.json do tema ativo. Pré-visualize a sua loja depois.",
    shopify_theme_autopilot: "Auto-piloto do tema",
    shopify_theme_autopilot_hint: "Aplicar TODOS os findings do tema conforme a severidade mínima escolhida em 1 clique",
    shopify_theme_autopilot_confirm: "O Auto-piloto do tema enviará {count} correções (severidade ≥ {sev}) de {total} findings ao tema publicado. Continuar?",
    shopify_theme_autopilot_nothing: "Nenhum finding do tema corresponde ao limite de severidade escolhido.",
    apply_log_title: "Registo de correções (audit trail)",
    apply_log_refresh: "Atualizar",
    apply_log_empty: "Ainda sem correções enviadas. Aplique uma a partir de Audit, Recusa ou Scan Shopify e aparecerá aqui.",
    apply_log_export_pdf: "Descarregar PDF",
    apply_log_hint: "Todas as correções enviadas ao Shopify são registadas aqui. Use como prova num pedido de reavaliação Google Merchant.",
    shopify_autopilot: "Auto-piloto",
    shopify_autopilot_hint: "Selecionar e aplicar TODAS as correções não conformes em 1 clique",
    shopify_autopilot_confirm: "O Auto-piloto enviará {fields} correções em {resources} recursos (pontuação ≤ {threshold}) ao Shopify. {skipped} recursos acima do limite serão ignorados. Continuar?",
    shopify_autopilot_done: "Auto-piloto concluído",
    shopify_autopilot_nothing: "Nada a corrigir — os recursos analisados estão conformes.",
    rejection_history: "Diagnósticos de recusa",
    rejection_history_empty: "Ainda sem diagnósticos. Inicie a primeira análise.",
    rejection_history_load: "Carregar",
    rejection_history_delete: "Eliminar",
    rejection_history_confirm_delete: "Eliminar este relatório de diagnóstico?",
    rejection_history_rewrites: "reescritas",
    apply_all_modal_title: "Aplicar tudo ao Shopify",
    apply_all_modal_submit: "Aplicar tudo",
    apply_all_rewrites_header: "Reescritas a enviar",
    apply_all_no_selection: "Nenhuma reescrita selecionada.",
    apply_all_skipped: "Reescritas ignoradas (frase não encontrada)",
    apply_all_btn: "Aplicar tudo ao Shopify",
    rejection_export_pdf: "Descarregar PDF",
    shopify_theme_scan: "Auditar tema",
    shopify_theme_scanning: "A auditar tema…",
    shopify_theme_scan_hint: "Auditoria só de leitura das definições do tema publicado (hero, banners, rodapé…)",
    shopify_theme_scan_results: "Auditoria do tema",
    shopify_theme_strings_scanned: "Cadeias analisadas",
    shopify_theme_findings: "Constatações",
    shopify_theme_no_findings: "Sem violações no tema — o seu tema publicado está conforme.",
    shopify_theme_readonly_title: "Auditoria só de leitura",
    shopify_theme_readonly_hint: "As correções não são aplicadas automaticamente ao tema. Abra Shopify › Loja online › Personalizar e edite cada secção.",
  },
  de: {
    rejection_tab: "Ablehnung",
    rejection_title: "Google Merchant Ablehnungs-Resolver",
    rejection_email_label: "Google-Ablehnungs-E-Mail einfügen",
    rejection_email_ph: "Fügen Sie hier die vollständige E-Mail von Google Merchant Center ein...",
    rejection_email_required: "Bitte fügen Sie die Google-E-Mail ein",
    rejection_product_url: "URL des abgelehnten Produkts",
    rejection_product_text: "Text des abgelehnten Produkts",
    rejection_product_text_ph: "Titel + Beschreibung des abgelehnten Produkts einfügen...",
    rejection_product_type: "Produkttyp (optional)",
    rejection_product_type_ph: "z.B. Schmuck, Kosmetik, Nahrungsergänzung...",
    rejection_run: "Diagnose & korrigieren",
    rejection_running: "Analyse...",
    rejection_rewrites: "Probleme & Umformulierungen",
    rejection_no_issues: "Keine eindeutigen Auslöser erkannt. Überprüfen Sie den gesamten Katalog oder kontaktieren Sie Google.",
    rejection_appeal_title: "Einspruchsmail an Google",
    rejection_checklist: "Die 8 Google-Unterkategorien „Falsche Angaben\"",
    rejection_checklist_intro: "Prüfen Sie jeden Punkt in Ihrem gesamten Katalog:",
    rejection_policy_link: "Offizielle Google-Richtlinie lesen",
    rejection_cat_1: "Nicht verfügbare Promotions oder Angebote (falsche Geschenke, fehlender Bestand)",
    rejection_cat_2: "Falsche Preise oder Gebühren (Preis abweichend vom Checkout, versteckte Kosten)",
    rejection_cat_3: "Irreführende Empfehlungen oder Partnerschaften (falsche Bewertungen, gefälschte Auszeichnungen, ungeprüfte Expertenaussagen)",
    rejection_cat_4: "Falsche oder irreführende Produktangaben (Wunderheilungen, absolute Superlative, unbelegtes „100 % authentisch\")",
    rejection_cat_5: "Irreführende Rabatte oder Promotions (falsche Streichpreise, falsche -80 %, ungültige Codes)",
    rejection_cat_6: "Irreführende Verfügbarkeit / Knappheit („nur 2 übrig\", falsche Dringlichkeit, falsche Countdowns)",
    rejection_cat_7: "Versteckte oder unklare Zahlungs-, Versand- oder Rückgabebedingungen",
    rejection_cat_8: "Versteckte oder falsch dargestellte Geschäftsidentität (fehlender Firmenname, Adresse, Kontakt)",
    cancel: "Abbrechen",
    apply_modal_title: "Korrektur auf Shopify anwenden",
    apply_modal_snippet: "Zu sendende Umformulierung",
    apply_modal_connection: "Shopify-Shop",
    apply_modal_resource_type: "Ressourcentyp",
    apply_modal_search: "Ressource suchen",
    apply_modal_search_ph: "Nach Titel suchen…",
    apply_modal_no_results: "Keine Ergebnisse. Suche anpassen.",
    apply_modal_field: "Zielfeld",
    apply_modal_field_hint: "Wir suchen den Originaltext in diesem Feld und ersetzen ihn durch die vorgeschlagene Version. Der restliche Inhalt bleibt erhalten.",
    apply_modal_applying: "Wird gesendet…",
    apply_modal_submit: "Auf Shopify anwenden",
    apply_no_conn: "Kein Shopify-Shop verbunden. Verbinden Sie zuerst einen über den Shopify-Tab.",
    apply_phrase_not_found: "Der Originaltext wurde im Shopify-Feld nicht gefunden. Bearbeiten Sie ihn manuell oder wählen Sie eine andere Ressource bzw. ein anderes Feld.",
    diff_inline: "Inline-Diff",
    diff_inline_hint: "Wort-für-Wort-Hervorhebung von Entfernungen/Hinzufügungen in einem Block",
    diff_inline_title: "Konforme Umformulierung — Inline-Diff",
    diff_side: "Nebeneinander",
    diff_side_hint: "Original und Vorschlag in zwei separaten Spalten anzeigen",
    diff_removed: "entfernt",
    diff_added: "hinzugefügt",
    product_picker_button: "Produkte auswählen",
    product_picker_hint: "Wählen Sie eine bestimmte Teilmenge der Produkte zum Scannen (statt der ersten N).",
    product_picker_title: "Produkte zum Scannen auswählen",
    product_picker_loaded: "geladen",
    product_picker_selected: "ausgewählt",
    product_picker_filter_placeholder: "Nach Titel filtern…",
    product_picker_select_visible: "Sichtbare auswählen",
    product_picker_unselect_visible: "Sichtbare abwählen",
    product_picker_clear: "Alle löschen",
    product_picker_loading: "Lade Produkte…",
    product_picker_no_match: "Keine Produkte passen zum Filter auf dieser Seite.",
    product_picker_empty: "Keine Produkte auf dieser Seite.",
    product_picker_prev: "Zurück",
    product_picker_next: "Nächste Seite",
    product_picker_page: "Seite",
    product_picker_confirm: "Auswahl verwenden",
    product_picker_picked_count: "Auswahl",
    product_picker_reset_hint: "Auswahl löschen (alles erneut scannen)",
    product_picker_reset_toast: "Auswahl gelöscht — Scan verwendet das Standardlimit.",
    product_picker_picked_toast: "Produkte ausgewählt",
    product_picker_filter_hint: "Produkte nach vorheriger Audit-Punktzahl filtern",
    product_picker_filter_all: "Alle Produkte",
    product_picker_filter_never: "Nie gescannt",
    product_picker_filter_lt90: "Punktzahl < 90",
    product_picker_filter_lt70: "Punktzahl < 70",
    product_picker_never_scanned: "Nie gescannt",
    product_picker_last_scanned: "Zuletzt gescannt",
    auto_route_title: "Auto-Routing — anwenden, wo der Text wirklich existiert",
    auto_route_hint: "Durchsucht alle Produkte, Seiten, Kollektionen, Richtlinien und Theme-Einstellungen; wendet jede Umformulierung dort an, wo der Originaltext gefunden wird. Empfohlen.",
    auto_route_or_manual: "Oder wählen Sie unten eine bestimmte Ressource manuell aus",
    auto_route_button: "Auto-Routing & Anwenden",
    auto_route_running: "Durchsuche den Shop…",
    auto_route_applied: "Angewendet",
    auto_route_not_found: "Nicht gefunden",
    auto_route_errors: "Fehler",
    auto_route_not_found_one: "Im Shop nicht gefunden.",
    auto_route_none_found: "Keine der Originaltexte wurde im Shop gefunden.",
    auto_route_partial_or_error: "Auto-Routing mit übersprungenen oder fehlerhaften Einträgen abgeschlossen — siehe Details.",
    auto_route_dry_run_button: "Nur Vorschau",
    auto_route_dry_run_hint: "Durchsucht den Shop, ohne etwas zu schreiben. Sie können prüfen, was geändert würde, bevor Sie anwenden.",
    auto_route_dry_run_done: "Vorschau abgeschlossen — Treffer gefunden",
    auto_route_dry_run_badge: "Vorschau — nichts wurde geändert",
    auto_route_dry_run_matches: "Würde angewendet auf",
    apply_success: "Korrektur auf Shopify angewendet",
    apply_updated_fields: "Aktualisierte Felder",
    apply_warn_title: "Nichts angewendet",
    apply_done_title: "Korrektur angewendet",
    apply_to_shopify_btn: "Auf Shopify anwenden",
    apply_replacements_count: "Ersetzte Phrasen",
    shopify_help_auto_title: "Automatisch verbinden (OAuth)",
    shopify_help_auto_step1: "Geben Sie die vollständige URL Ihres Shopify-Shops ein — Beispiel: meinshop.myshopify.com (ohne https://, ohne Pfad)",
    shopify_help_auto_step2: "Klicken Sie auf „Install with Shopify\" — ein neuer Tab öffnet die Shopify-Autorisierungsseite",
    shopify_help_auto_step3: "Melden Sie sich bei Shopify an (falls noch nicht) und bestätigen Sie mit „Install app\"",
    shopify_help_auto_step4: "Shopify leitet Sie zu MerchantAudit zurück. Die Verbindung erscheint oben in dieser Liste — fertig.",
    shopify_help_auto_troubleshoot: "Falls Shopify „redirect_uri is not whitelisted\" zurückgibt oder Sie nach Install hängen bleiben: Die OAuth-App ist nicht für DIESE Domain konfiguriert. Nutzen Sie Manual Custom App unten, oder aktualisieren Sie APP_PUBLIC_URL in Ihrer Produktionsumgebung und die Weiterleitungs-URL in Ihrer Shopify-Partners-App.",
    shopify_help_manual_title: "Manuell verbinden (Custom App)",
    shopify_help_manual_step1: "Öffnen Sie Ihren Shopify-Admin → Einstellungen (unten links) → Apps und Vertriebskanäle",
    shopify_help_manual_step2: "Klicken Sie auf „Apps entwickeln\". Falls die Schaltfläche fehlt, klicken Sie zuerst auf „Entwicklung eigener Apps zulassen\" und aktualisieren.",
    shopify_help_manual_step3: "Klicken Sie auf „App erstellen\" → wählen Sie einen Namen (z. B. „MerchantAudit\") → „Erstellen\"",
    shopify_help_manual_step4: "Tab „Konfiguration\" → „Admin-API-Zugriffsbereiche\" → „Konfigurieren\" → aktivieren: read_products, write_products, read_content, write_content, read_themes, write_themes, read_online_store_navigation, write_online_store_navigation. Speichern.",
    shopify_help_manual_step5: "Tab „API-Anmeldedaten\" → „App installieren\" → bestätigen",
    shopify_help_manual_step6: "Kopieren Sie das „Admin-API-Zugriffstoken\" (beginnt mit shpat_…) — es wird NUR EINMAL angezeigt, jetzt kopieren.",
    shopify_help_manual_step7: "Füllen Sie die drei Felder aus: ein Spitzname für die Verbindung, Ihre Shop-URL (xxx.myshopify.com), das Token. Klicken Sie auf „Connect\".",
    shopify_help_manual_troubleshoot: "Bei „Invalid API key or access token\": Ihr Token ist falsch oder Sie haben „App installieren\" vergessen. Bei „store_url must end with .myshopify.com\": geben Sie die URL ohne https:// und ohne abschließendes / ein.",
    shopify_token_warning_title: "Verwenden Sie kein Hoppscotch / Postman für OAuth",
    shopify_token_warning_body: "Das Token, das mit shpat_… beginnt, wird DIREKT in Ihrem Shopify-Admin angezeigt, auf dem Tab „API-Anmeldedaten\" NACHDEM Sie auf „App installieren\" geklickt haben. Sie müssen KEINEN manuellen OAuth-Austausch mit client_id + client_secret durchführen. Wenn Ihr Token mit shpss_… beginnt, ist das das Client Secret, NICHT das Zugriffs-Token.",
    shopify_health_ok: "Alles grün — OAuth + manuell + Theme-Audit bereit",
    shopify_health_warn: "Größtenteils OK — kleines Konfigurationsproblem",
    shopify_health_manual_only: "OAuth nicht konfiguriert — nur manuelle Custom-App-Verbindung",
    shopify_health_broken: "Konfiguration unvollständig — Shopify-Verbindung wird fehlschlagen",
    shopify_health_oauth_configured: "OAuth-Schlüssel konfiguriert",
    shopify_health_host_match: "App-URL passt zu dieser Domain",
    shopify_health_encryption: "Token-Verschlüsselung bereit",
    shopify_health_scope_read_themes: "Scope read_themes gewährt",
    shopify_health_redirect_uri: "Callback-URL zum Whitelisten",
    shopify_health_warn_hint: "Fügen Sie 'read_themes' zu SHOPIFY_APP_SCOPES hinzu und stellen Sie erneut bereit, um den Theme-Audit zu aktivieren.",
    shopify_health_manual_only_hint: "Setzen Sie SHOPIFY_API_KEY + SHOPIFY_API_SECRET in Ihrer Umgebung, um die 1-Klick-OAuth-Installation zu aktivieren. In der Zwischenzeit nutzen Sie die manuelle Custom App.",
    shopify_health_broken_hint: "Beheben Sie die roten Punkte oben und stellen Sie erneut bereit. Wahrscheinlichste Ursache: APP_PUBLIC_URL passt nicht zur aktuellen Domain.",
    shopify_theme_apply_one: "Anwenden",
    shopify_theme_apply_all: "Alles anwenden",
    shopify_theme_applied: "Angewendet",
    shopify_theme_apply_done: "Korrekturen auf Theme übertragen",
    shopify_theme_no_apply: "Keine Korrektur angewendet (Pfade stimmen nicht mit aktivem Theme überein).",
    shopify_theme_apply_warn_title: "Theme-Schreibzugriff aktiviert.",
    shopify_theme_apply_warn_hint: "Jedes Anwenden ändert config/settings_data.json des veröffentlichten Themes direkt. Vorschau Ihres Shops danach prüfen.",
    shopify_theme_autopilot: "Theme-Auto-Pilot",
    shopify_theme_autopilot_hint: "ALLE Theme-Findings nach der gewählten Mindest-Severität mit 1 Klick anwenden",
    shopify_theme_autopilot_confirm: "Theme-Auto-Pilot überträgt {count} Korrekturen (Severität ≥ {sev}) von {total} Findings an das veröffentlichte Theme. Fortfahren?",
    shopify_theme_autopilot_nothing: "Keine Theme-Findings entsprechen der gewählten Severität.",
    apply_log_title: "Korrektur-Log (Audit Trail)",
    apply_log_refresh: "Aktualisieren",
    apply_log_empty: "Noch keine Korrekturen übertragen. Wenden Sie eine aus Audit, Ablehnung oder Shopify-Scan an, dann erscheint sie hier.",
    apply_log_export_pdf: "PDF herunterladen",
    apply_log_hint: "Alle an Shopify übertragenen Korrekturen werden hier protokolliert. Als Nachweis für eine Google-Merchant-Überprüfungsanfrage nutzbar.",
    shopify_autopilot: "Auto-Pilot",
    shopify_autopilot_hint: "ALLE nicht-konformen Korrekturen mit 1 Klick auswählen und anwenden",
    shopify_autopilot_confirm: "Der Auto-Pilot überträgt {fields} Korrekturen an {resources} Ressourcen (Score ≤ {threshold}) zu Shopify. {skipped} Ressourcen über dem Schwellwert werden übersprungen. Fortfahren?",
    shopify_autopilot_done: "Auto-Pilot abgeschlossen",
    shopify_autopilot_nothing: "Nichts zu korrigieren — Ihre gescannten Ressourcen sind konform.",
    rejection_history: "Ablehnungsdiagnosen",
    rejection_history_empty: "Noch keine Diagnosen. Starten Sie Ihre erste Analyse.",
    rejection_history_load: "Laden",
    rejection_history_delete: "Löschen",
    rejection_history_confirm_delete: "Diesen Diagnosebericht löschen?",
    rejection_history_rewrites: "Umformulierungen",
    apply_all_modal_title: "Alles auf Shopify anwenden",
    apply_all_modal_submit: "Alles anwenden",
    apply_all_rewrites_header: "Zu sendende Umformulierungen",
    apply_all_no_selection: "Keine Umformulierung ausgewählt.",
    apply_all_skipped: "Übersprungene Umformulierungen (Phrase nicht gefunden)",
    apply_all_btn: "Alles auf Shopify anwenden",
    rejection_export_pdf: "PDF herunterladen",
    shopify_theme_scan: "Theme auditieren",
    shopify_theme_scanning: "Theme wird auditiert…",
    shopify_theme_scan_hint: "Schreibgeschützte Auditierung Ihrer veröffentlichten Theme-Einstellungen (Hero, Banner, Footer…)",
    shopify_theme_scan_results: "Theme-Audit",
    shopify_theme_strings_scanned: "Geprüfte Texte",
    shopify_theme_findings: "Befunde",
    shopify_theme_no_findings: "Keine Theme-Verstöße erkannt — Ihr veröffentlichtes Theme ist konform.",
    shopify_theme_readonly_title: "Schreibgeschützte Auditierung",
    shopify_theme_readonly_hint: "Korrekturen werden nicht automatisch im Theme angewendet. Öffnen Sie Shopify › Online Store › Anpassen und bearbeiten Sie jede Sektion.",
  },
};

Object.keys(REJECTION_KEYS).forEach((lng) => {
  KEYS[lng] = { ...KEYS[lng], ...REJECTION_KEYS[lng] };
});

export function t(lang, key) {
  const dict = KEYS[lang] || KEYS.en;
  return dict[key] || KEYS.en[key] || key;
}
